/**
 * TrackerReviewTab - Review and Approve submissions within Tracker
 * This integrates the review functionality into the working Tracker Dashboard
 */

import React, { useMemo, useState, useEffect } from "react";
import { useSearch } from "wouter";
import { readStoreReviewScope } from "@/lib/storeReviewPriority";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { TrackerLayout } from "./TrackerLayout";
import { readPersistedState, writePersistedState } from "./TrackerLayout";
import { getJson, http, apiFetch } from "@/lib/http";
import { copyTaskDefinitionIdsToParams } from "@/lib/trackerTaskDefinitionFilter";
import {
    rejectFollowUpActionForType,
    REJECTION_TYPE_DISPLAY_ORDER,
    type RejectionType,
} from "@shared/rejection-types";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { NewFeatureBadge } from "@/components/tracker/NewFeatureBadge";
import { Textarea } from "@/components/ui/textarea";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent as AlertDialogModalContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import {
    Trash2,
    Package,
    CheckCircle,
    XCircle,
    Clock,
    Image as ImageIcon,
    Loader2,
    Eye,
    MapPin,
    Calendar,
    User,
    CheckCircle2,
    ChevronRight,
    AlertTriangle,
    ArrowUpDown,
    Star,
    Pencil,
    Check,
    X,
    History,
    ShieldCheck,
    Upload,
} from "lucide-react";
import { format, formatDistanceToNow } from "date-fns";
import DOMPurify from "dompurify";
import { useToast } from "@/hooks/use-toast";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { DataTable } from "@/components/tables/data-table";
import { ColumnDef } from "@tanstack/react-table";
import { Checkbox } from "@/components/ui/checkbox";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuthUser } from "@/hooks/useAuthUser";
import AiTriageFeedbackPanel from "@/components/ai/AiTriageFeedbackPanel";
import VerificationSummaryPanel from "@/components/verification/VerificationSummaryPanel";
import { RemovalSourcePostingReferences } from "@/components/proof-of-posting/RemovalSourcePostingReferences";
import { CurrentSubmissionEvidencePanel } from "@/components/proof-of-posting/CurrentSubmissionEvidencePanel";
import { getReviewDecisionScopeHint, getReferenceKindGroupCount } from "@/components/proof-of-posting/submissionReferenceLabels";
import HeldEvidenceReviewModal from "./HeldEvidenceReviewModal";
import {
    buildCombinedReviewTableRows,
    buildReviewPendingTotals,
    describeCombinedTablePaginationNote,
    describeReviewQueueEmptyMessage,
    isHeldEvidenceBlockedByIncludeReviewed,
    resolveReviewPendingPageCount,
    type ReviewPendingPaginationMode,
    type ReviewQueueTypeFilter,
} from "@shared/review-pending-pagination";
import { heldEvidenceQueueTypeLabel, heldEvidenceReviewStatusLabel } from "@shared/held-evidence-review-labels";
import { isReviewPresenceEnabled } from "@/lib/reviewPresenceFlag";
import { useReviewPresenceLifecycle } from "@/hooks/useReviewPresenceLifecycle";
import {
    optimisticallyRemoveReviewPendingSubmission,
    scheduleReviewDecisionBackgroundSync,
} from "@/lib/reviewPendingOptimisticUpdate";
import { InstallExceptionPanel, type ReviewInstallException } from "./InstallExceptionPanel";

class ReviewDecisionError extends Error {
    errorCode?: string;
    repostTaskId?: number | null;

    constructor(message: string, options?: { errorCode?: string; repostTaskId?: number | null }) {
        super(message);
        this.name = "ReviewDecisionError";
        this.errorCode = options?.errorCode;
        this.repostTaskId = options?.repostTaskId ?? null;
    }
}

function formatReviewDecisionError(errorData: any, fallback: string) {
    const errorCode = typeof errorData?.errorCode === "string" ? errorData.errorCode : undefined;
    const repostTaskId = typeof errorData?.repostTaskId === "number" ? errorData.repostTaskId : null;
    if (errorCode === "REPOST_IN_PROGRESS") {
        const taskLabel = repostTaskId ? ` #${repostTaskId}` : "";
        return {
            message: `This original submission cannot be approved because repost task${taskLabel} is already in progress. Open/review or cancel that repost task first, then retry approving the original.`,
            errorCode,
            repostTaskId,
        };
    }

    return {
        message: typeof errorData?.error === "string" && errorData.error.trim().length > 0 ? errorData.error : fallback,
        errorCode,
        repostTaskId,
    };
}

interface Submission {
    queueKind?: "submission" | "held";
    mediaOperationId?: string;
    postingEventId: number;
    taskId: number;
    storeName: string;
    campaignName: string;
    assignmentId: number | null;
    operatorName: string;
    postedUnits: number;
    timestamp: string;
    imageCount: number;
    storeId: number;
    campaignId: number;
    cycleId: number;
    taskType: string;
    hasDiscrepancy: boolean;
    latestDecision: "APPROVE" | "REJECT" | null;
    heldStatus?: "awaiting_upload" | "awaiting_release" | "needs_assignment" | "beyond_sla";
    aiVerdict?: "PASS" | "PARTIAL_FAIL" | "FAIL" | null;
    aiVerdictReasons?: string[] | null;
}

interface QuantityCorrection {
    id: number;
    formatEvidenceId: number;
    formatId: number;
    oldActualCount: number | null;
    newActualCount: number;
    correctedByUserId: number;
    correctedByName: string;
    reason: string | null;
    createdAt: string;
}

interface FormatEvidence {
    id: number;
    formatId: number | null;
    formatName: string;
    expectedCount: number;
    actualCount: number | null;
    discrepancyReason: string | null;
    hasCorrection?: boolean;
    latestCorrection?: QuantityCorrection | null;
}

type ReviewFormatConfirmationStatus = "CONFIRMED" | "ISSUE" | "UNCLEAR";
type ReviewFormatConfirmationSource = "SYSTEM_PREFILL" | "REVIEWER_CONFIRMED" | "REVIEWER_EDITED";

interface FormatRowConfirmation {
    formatEvidenceId: number;
    formatId: number | null;
    formatName: string;
    expectedCount: number;
    submittedCount: number;
    prefillStatus: ReviewFormatConfirmationStatus;
    finalStatus: ReviewFormatConfirmationStatus | null;
    reviewSource: ReviewFormatConfirmationSource;
    notes: string | null;
    aiPresenceSupport?: "supported" | "unsupported" | "unclear" | "not_assessable" | "not_assessed";
    aiCountSupport?: "full" | "partial" | "none" | "unclear" | "not_assessable" | "not_assessed";
}

interface SubmissionDetail {
    submission: {
        id: number;
        taskId: number;
        campaignId: number;
        postedUnits: number;
        timestamp: string;
        notes: string | null;
        installerNote: string | null;
        storeName: string;
        storeAddress: string | null;
        campaignName: string;
        assignmentId: number | null;
        operatorName: string;
        cycleId: number;
        packName: string | null;
        taskType: string;
        postingInstructionsText: string | null;
        postingInstructionsImageUrl: string | null;
    };
    aiVerdict?: "PASS" | "PARTIAL_FAIL" | "FAIL" | null;
    aiVerdictReasons?: string[] | null;
    aiVerdictComputedAt?: string | null;
    images: Array<{
        id: number;
        secureUrl: string;
        assetType: string;
    }>;
    latestDecision: ReviewDecisionSummary | null;
    formatEvidence: FormatEvidence[];
    installException?: ReviewInstallException | null;
    formatRowConfirmations?: FormatRowConfirmation[];
    formatContext?: {
        mediaFormatId: number | null;
        expectedFormatName: string | null;
        expectedQuantity: number | null;
        reviewerDescription: string | null;
        typicalDimensions: string | null;
        placementGuidance: string | null;
        visualCues: string[];
        reviewChecklist: string[];
        exampleImages: Array<{
            id: number;
            imageUrl: string;
            role: "good_example" | "bad_example" | "borderline";
            caption: string | null;
        }>;
    } | null;
    expectedFormatContexts?: Array<{
        mediaFormatId: number | null;
        expectedFormatName: string;
        expectedQuantity: number;
        reviewerDescription: string | null;
        typicalDimensions: string | null;
        placementGuidance: string | null;
        visualCues: string[];
        reviewChecklist: string[];
        exampleImages: Array<{
            id: number;
            imageUrl: string;
            role: "good_example" | "bad_example" | "borderline";
            caption: string | null;
        }>;
    }>;
    corrections?: QuantityCorrection[];
    favouriteImageIds?: number[];
    aiVerification?: {
        id: number;
        presenceStatus: "MATCH" | "PARTIAL_MATCH" | "MISMATCH" | "INSUFFICIENT_EVIDENCE";
        qualityStatus: "NO_ISSUE_DETECTED" | "POSSIBLE_ISSUE" | "CONFIRMED_ISSUE" | "INSUFFICIENT_EVIDENCE";
        overallStatus: "PASS" | "REVIEW_REQUIRED" | "FAIL" | "INSUFFICIENT_EVIDENCE";
        evidenceSufficiency: "SUFFICIENT" | "INSUFFICIENT";
        confidence: number;
        unknownFormatCount: number;
        explanation?: string | null;
    } | null;
    aiVerificationOverrides?: Array<{
        id: number;
        targetType: "presence" | "quality" | "detection" | "support_status";
        fromStatus: string | null;
        toStatus: string;
        reasonCode: string | null;
        comment: string | null;
        decidedByName?: string | null;
        createdAt: string;
    }>;
    formatReviewLabels?: Array<{
        id: number;
        mediaFormatId: number;
        reviewDecision: "APPROVE" | "REJECT";
        expectedFormatMatch: "MATCH" | "MISMATCH" | "UNCLEAR";
        notes: string | null;
        allEvidenceImageIds: number[];
        supportingEvidenceImageIds: number[];
        createdAt: string;
    }>;
    verificationSummary?: {
        id: number;
        postingEventId: number;
        engineVersion: string;
        expectedContext?: {
            expectedFormats?: Array<{
                formatId: number | null;
                formatName: string;
                expectedQuantity: number;
            }>;
        };
        overallVerificationRisk: number;
        riskBand: "LOW" | "MEDIUM" | "HIGH";
        primaryReasonFlags: string[];
        assessmentSummary: string | null;
        systemSignals: {
            expected_format_support: "STRONG_SUPPORT" | "PARTIAL_SUPPORT" | "WEAK_SUPPORT" | "NO_SUPPORT" | "INSUFFICIENT_EVIDENCE";
            quantity_support: "STRONG_SUPPORT" | "PARTIAL_SUPPORT" | "WEAK_SUPPORT" | "NO_SUPPORT" | "INSUFFICIENT_EVIDENCE";
            image_quality: "ACCEPTABLE" | "POOR" | "UNCLEAR";
            evidence_sufficiency: "SUFFICIENT" | "INSUFFICIENT";
            format_signals?: Array<{
                format_id: number | null;
                format_name: string;
                presence_support: "supported" | "unsupported" | "unclear" | "not_assessable";
                count_support: "full" | "partial" | "none" | "unclear" | "not_assessable";
            }>;
            overall_verification_risk: number;
            risk_band: "LOW" | "MEDIUM" | "HIGH";
            primary_reason_flags: string[];
            assessment_summary: string;
            inputs_state: "TRIAGE_ONLY" | "AI_VERIFICATION_ONLY" | "TRIAGE_AND_AI_VERIFICATION" | "MINIMAL_FALLBACK";
        };
    } | null;
    activeRetailerStandards?: {
        id: number;
        version: number;
        installationStandards: string;
        removalStandards: string;
        conductInStore: string;
        submissionRequirements: string;
        imageQualityForReview: string;
    } | null;
    sourcePostingReferences?: Array<{
        formatId: number;
        formatName: string | null;
        postingEventId: number | null;
        imageUrl: string | null;
        approvedAt: string | null;
        postingOperatorName: string | null;
        sourceType: 'confirmed_delivery' | 'planned_delivery' | 'rejected_submission' | null;
        referenceKind?: 'removal_source' | 'repost_rejected' | 'repost_rejected_removal';
    }>;
}

interface ReviewDecisionSummary {
    id: number;
    decision: "APPROVE" | "REJECT";
    expectedFormatMatch?: "MATCH" | "MISMATCH" | "UNCLEAR" | null;
    expectedFormatMatchNotes?: string | null;
    rejectionType?: RejectionType | null;
    feedbackText?: string | null;
    reasonCodes?: string[] | null;
    rejectionNotes?: string | null;
    rejectionReasonLabelsSnapshot?: string[] | null;
    followUpAction?: "CREATE_REPOST" | "ADVISORY_FEEDBACK" | null;
    retailerReady?: boolean;
    adminNotes?: string | null;
    decidedByUserId: number;
    decidedByName?: string | null;
    createdAt: string;
}

type ExpectedFormatMatch = "MATCH" | "MISMATCH" | "UNCLEAR";

function getRejectionTypeLabel(rejectionType?: RejectionType | null): string {
    switch (rejectionType) {
        case "PHOTO_ONLY":
            return "Photo retake only";
        case "REVISIT":
            return "Physical revisit - correct installation";
        case "REVISIT_WITH_SIGNOFF":
            return "Physical revisit - refit + manager sign-off";
        case "ADVISORY_ONLY":
            return "Accepted with concerns — no operator work";
        default:
            return "Rejection";
    }
}

interface PendingResponse {
    success: boolean;
    data: Submission[];
    held?: Submission[];
    submissionTotal?: number;
    heldTotal?: number;
    heldUploadPendingTotal?: number;
    combinedTotal?: number;
    total?: number;
    pagination: {
        limit: number;
        offset: number;
        mode?: ReviewPendingPaginationMode;
    };
}

function partitionHeldQueueRows(rows: Submission[]): {
    reviewReady: Submission[];
    uploadPending: Submission[];
} {
    const reviewReady: Submission[] = [];
    const uploadPending: Submission[] = [];
    for (const row of rows) {
        if (row.heldStatus === "awaiting_upload") {
            uploadPending.push(row);
        } else {
            reviewReady.push(row);
        }
    }
    return { reviewReady, uploadPending };
}

function resolveHeldRowsForQueueView(input: {
    typeFilter: ReviewQueueTypeFilter;
    paginationMode: ReviewPendingPaginationMode;
    heldRows: Submission[];
}): Submission[] {
    const { reviewReady, uploadPending } = partitionHeldQueueRows(input.heldRows);

    if (input.typeFilter === "held_upload_pending") {
        return input.paginationMode === "heldUploadPendingOnly" ? input.heldRows : uploadPending;
    }
    if (input.typeFilter === "held") {
        return input.paginationMode === "heldOnly" ? input.heldRows : reviewReady;
    }
    return reviewReady;
}

function mapHeldRowToSubmission(row: Submission): Submission {
    return {
        ...row,
        queueKind: "held",
        postingEventId: 0,
        postedUnits: 0,
        campaignId: row.campaignId ?? 0,
        hasDiscrepancy: false,
        latestDecision: null,
    };
}

type NotesRequirement = "NONE" | "OPTIONAL" | "REQUIRED";

function RejectionTypeSelectItems() {
    return (
        <>
            {REJECTION_TYPE_DISPLAY_ORDER.map((value) => (
                <SelectItem key={value} value={value}>
                    <span className="inline-flex items-center gap-1.5">
                        {getRejectionTypeLabel(value)}
                        {value === "ADVISORY_ONLY" && <NewFeatureBadge />}
                    </span>
                </SelectItem>
            ))}
        </>
    );
}

interface ReasonCode {
    code: string;
    label: string;
    appliesTo: RejectionType[];
    notesRequirement: NotesRequirement;
    sortOrder: number;
}

function formatConfirmationLabel(value: ReviewFormatConfirmationStatus): string {
    if (value === "CONFIRMED") return "Yes";
    if (value === "ISSUE") return "No";
    return "Unclear";
}

function isRemovalTaskType(taskType?: string | null): boolean {
    return (taskType || "").toUpperCase() === "REMOVAL";
}

function isRepostTaskType(taskType?: string | null): boolean {
    return (taskType || "").toUpperCase() === "REPOST";
}

function showsSubmissionReferencePanel(taskType?: string | null): boolean {
    return isRemovalTaskType(taskType) || isRepostTaskType(taskType);
}

function countReferenceKindGroups(references?: SubmissionDetail['sourcePostingReferences']): number {
    return getReferenceKindGroupCount(references ?? []);
}

function getClaimedSubmissionTitle(taskType?: string | null): string {
    return isRemovalTaskType(taskType) ? "Claimed Removal" : "Claimed Delivery";
}

function getClaimedSubmissionActionLabel(taskType?: string | null): string {
    return isRemovalTaskType(taskType) ? "Mark all removed" : "Mark all present";
}

function getReportedUnitsLabel(taskType?: string | null): string {
    return isRemovalTaskType(taskType) ? "Reported removed units" : "Reported units";
}

function getReportedCountBadgeLabel(taskType?: string | null): string {
    return isRemovalTaskType(taskType) ? "Removed" : "Reported";
}

function getFormatConfirmationSubjectLabel(taskType?: string | null): string {
    return isRemovalTaskType(taskType) ? "Removed" : "Present";
}

function getQuantityLabel(taskType?: string | null): string {
    return isRemovalTaskType(taskType) ? "Removed quantity" : "Delivered quantity";
}

function getAiFeedbackStatusLabel(
    expectedFormatCount: number,
    labeledFormatCount: number,
): "Not started" | "Partial" | "Complete" {
    if (labeledFormatCount <= 0) return "Not started";
    if (expectedFormatCount > 0 && labeledFormatCount >= expectedFormatCount) return "Complete";
    return "Partial";
}

function formatConfirmationVariant(value: ReviewFormatConfirmationStatus): "default" | "destructive" | "secondary" {
    if (value === "CONFIRMED") return "secondary";
    if (value === "ISSUE") return "destructive";
    return "default";
}

function formatAiPresenceLabel(value?: FormatRowConfirmation["aiPresenceSupport"], taskType?: string | null): string {
    const subject = isRemovalTaskType(taskType) ? "removed" : "present";
    switch (value) {
        case "supported":
            return `AI: ${subject} supported`;
        case "unsupported":
            return `AI: ${subject} unsupported`;
        case "unclear":
            return `AI: ${subject} unclear`;
        case "not_assessable":
            return "AI: not assessable";
        default:
            return "AI: not assessed";
    }
}

function formatAiCountLabel(value?: FormatRowConfirmation["aiCountSupport"]): string {
    switch (value) {
        case "full":
            return "count full";
        case "partial":
            return "count partial";
        case "none":
            return "count none";
        case "unclear":
            return "count unclear";
        case "not_assessable":
            return "count not assessable";
        default:
            return "count not assessed";
    }
}

function roleLabel(role: "good_example" | "bad_example" | "borderline"): string {
    if (role === "bad_example") return "Bad";
    if (role === "borderline") return "Borderline";
    return "Good";
}

type ReviewQueueToolbarProps = {
    includeReviewed: boolean;
    onIncludeReviewedChange: (value: boolean) => void;
    typeFilter: ReviewQueueTypeFilter;
    onTypeFilterChange: (value: ReviewQueueTypeFilter) => void;
    ordinaryOnly?: boolean;
};

function ReviewQueueToolbar({
    includeReviewed,
    onIncludeReviewedChange,
    typeFilter,
    onTypeFilterChange,
    ordinaryOnly = false,
}: ReviewQueueToolbarProps) {
    return (
        <div className="flex items-center gap-3 flex-wrap">
            <label className="flex items-center gap-2 text-sm text-muted-foreground">
                <Checkbox
                    checked={includeReviewed}
                    onCheckedChange={(value) => onIncludeReviewedChange(Boolean(value))}
                />
                Include reviewed
            </label>
            <Select value={typeFilter} onValueChange={(value) => onTypeFilterChange(value as ReviewQueueTypeFilter)}>
                <SelectTrigger className="h-8 w-[148px]">
                    <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                    <SelectItem value="all">All types</SelectItem>
                    <SelectItem value="posting">Posting</SelectItem>
                    <SelectItem value="removal">Removal</SelectItem>
                    {!ordinaryOnly && <SelectItem value="held">Held photos — ready to review</SelectItem>}
                    {!ordinaryOnly && <SelectItem value="held_upload_pending">Waiting for upload</SelectItem>}
                </SelectContent>
            </Select>
        </div>
    );
}

function ReviewQueueStatBadge({
    icon: Icon,
    count,
    label,
    active = false,
    tone = "default",
}: {
    icon: React.ComponentType<{ className?: string }>;
    count: number;
    label: string;
    active?: boolean;
    tone?: "default" | "held";
}) {
    const toneClass = tone === "held"
        ? active
            ? "border-teal-300 bg-teal-50 text-teal-900"
            : "border-teal-100 text-teal-800"
        : active
            ? "border-orange-300 bg-orange-50 text-orange-900"
            : "text-foreground";

    return (
        <Badge variant="outline" className={`gap-1.5 px-2.5 py-1 font-normal ${toneClass}`}>
            <Icon className="h-3.5 w-3.5" />
            <span className="font-semibold tabular-nums">{count}</span>
            <span>{label}</span>
        </Badge>
    );
}

export default function TrackerReviewTab() {
    const locationSearch = useSearch();
    const storeScope = readStoreReviewScope(locationSearch);
    const { storeId, campaignId } = storeScope;
    const searchParams = new URLSearchParams(locationSearch);
    const cycleId = searchParams.get("cycleId");
    const retailerId = searchParams.get("retailerId");
    const taskDefinitionIds = searchParams.get("taskDefinitionIds");
    const submissionIdParam = searchParams.get("submissionId");
    const initialSubmissionId = submissionIdParam ? parseInt(submissionIdParam) : null;

    const queryClient = useQueryClient();
    const { toast } = useToast();
    const { user } = useAuthUser();

    // State for the review modal
    const [selectedSubmission, setSelectedSubmission] = useState<number | null>(
        initialSubmissionId && !isNaN(initialSubmissionId) ? initialSubmissionId : null
    );

    const reviewPresenceCycleId = useMemo(() => {
        if (!cycleId || cycleId === "all") return null;
        const parsed = Number(cycleId);
        return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
    }, [cycleId]);

    const reviewPresence = useReviewPresenceLifecycle({
        enabled: isReviewPresenceEnabled(),
        cycleId: reviewPresenceCycleId,
        surfaceActive: selectedSubmission != null,
    });

    useEffect(() => {
        if (!selectedSubmission || !isReviewPresenceEnabled() || reviewPresenceCycleId == null) {
            return;
        }
        void reviewPresence.focusReviewQueue(selectedSubmission);
    }, [selectedSubmission, reviewPresenceCycleId, reviewPresence]);

    const [selectedHeldMediaOperationId, setSelectedHeldMediaOperationId] = useState<string | null>(null);
    const [feedback, setFeedback] = useState("");
    const [reviewDecision, setReviewDecision] = useState<"APPROVE" | "REJECT" | "APPROVE_RETAILER_READY">("APPROVE");
    const [guidanceOpen, setGuidanceOpen] = useState(false);
    const [approveInsteadOpen, setApproveInsteadOpen] = useState(false);
    const [expectedFormatMatch, setExpectedFormatMatch] = useState<ExpectedFormatMatch>("MATCH");
    const [expectedFormatMatchNotes, setExpectedFormatMatchNotes] = useState("");
    const [selectedRejectionType, setSelectedRejectionType] = useState<RejectionType | null>(null);
    const [rejectionNotes, setRejectionNotes] = useState("");
    const [selectedReasons, setSelectedReasons] = useState<string[]>([]);
    const [formatConfirmationStates, setFormatConfirmationStates] = useState<Record<number, ReviewFormatConfirmationStatus>>({});
    const [reviewModalTab, setReviewModalTab] = useState<"compliance" | "ai-feedback">("compliance");
    const [selectedTrainingFormatId, setSelectedTrainingFormatId] = useState<string>("");

    // Restore sub-tab state from localStorage (defensive: fall back to defaults)
    const _persisted = readPersistedState();
    const _reviewSub = _persisted?.subTabs?.review;
    const _initTypeFilter: ReviewQueueTypeFilter =
        !storeScope.scoped && (_reviewSub?.typeFilter === "posting"
            || _reviewSub?.typeFilter === "removal"
            || _reviewSub?.typeFilter === "held"
            || _reviewSub?.typeFilter === "held_upload_pending")
            ? _reviewSub.typeFilter
            : "all";
    const _initIncludeReviewed: boolean = !storeScope.scoped && _reviewSub?.includeReviewed === true;

    const [typeFilter, setTypeFilterRaw] = useState<ReviewQueueTypeFilter>(_initTypeFilter);
    const [includeReviewed, setIncludeReviewedRaw] = useState(_initIncludeReviewed);
    const [searchTerm, setSearchTerm] = useState(() => new URLSearchParams(window.location.search).get("search") ?? "");
    const [reviewPage, setReviewPage] = useState(1);
    const [reviewPageSize, setReviewPageSize] = useState(50);
    // A new store navigation must not inherit a previous store's search/type/page.
    useEffect(() => {
        if (!storeScope.scoped) return;
        setTypeFilterRaw('all');
        setIncludeReviewedRaw(false);
        setSearchTerm('');
        setReviewPage(1);
        setSelectedSubmission(null);
    }, [storeId, campaignId, cycleId, retailerId, storeScope.scoped]);

    // Wrappers that persist on change
    const setTypeFilter = (v: ReviewQueueTypeFilter) => {
        setTypeFilterRaw(v);
        writePersistedState({ subTabs: { review: { typeFilter: v, includeReviewed } } });
    };
    const setIncludeReviewed = (v: boolean) => {
        setIncludeReviewedRaw(v);
        writePersistedState({ subTabs: { review: { typeFilter, includeReviewed: v } } });
    };
    const [favouriteImageIds, setFavouriteImageIds] = useState<number[]>([]);
    const [supportingFormatImageIds, setSupportingFormatImageIds] = useState<number[]>([]);

    // State for inline quantity editing (single format)
    const [editingFormatId, setEditingFormatId] = useState<number | null>(null);
    const [editValue, setEditValue] = useState<string>("");

    // State for bulk correction mode
    const [correctionMode, setCorrectionMode] = useState(false);
    const [correctionValues, setCorrectionValues] = useState<Record<number, string>>({});

    // Build query string preserving both retailerId and cycleId
    const cycleQueryObj = new URLSearchParams();
    if (retailerId) cycleQueryObj.set("retailerId", retailerId);
    if (cycleId) cycleQueryObj.set("cycleId", cycleId);
    copyTaskDefinitionIdsToParams(cycleQueryObj, window.location.search);

    useEffect(() => {
        setReviewPage(1);
    }, [retailerId, cycleId, includeReviewed, typeFilter, searchTerm, taskDefinitionIds, storeId, campaignId]);

    const pendingQueryKey = useMemo(
        () =>
            [
                "/api/review/pending",
                retailerId,
                cycleId,
                includeReviewed,
                typeFilter,
                searchTerm,
                reviewPage,
                reviewPageSize,
                taskDefinitionIds,
                storeId,
                campaignId,
            ] as const,
        [
            retailerId,
            cycleId,
            includeReviewed,
            typeFilter,
            searchTerm,
            reviewPage,
            reviewPageSize,
            taskDefinitionIds,
            storeId,
            campaignId,
        ],
    );

    // Fetch pending submissions
    const { data: pendingData, isLoading, error, refetch, isRefetching } = useQuery<PendingResponse>({
        queryKey: pendingQueryKey,
        queryFn: async () => {
            const params = new URLSearchParams();
            if (cycleId && cycleId !== "all") params.append("cycleId", cycleId);
            if (retailerId && retailerId !== "all") params.append("retailerId", retailerId);
            if (storeId) { params.set('storeId', storeId); params.set('includeHeld', '0'); }
            if (campaignId && campaignId !== 'all') params.set('campaignId', campaignId);
            if (includeReviewed) params.append("includeReviewed", "1");
            if (typeFilter === "posting") params.append("taskType", "POSTING");
            if (typeFilter === "removal") params.append("taskType", "REMOVAL");
            if (typeFilter === "held") params.append("heldOnly", "1");
            if (typeFilter === "held_upload_pending") params.append("heldUploadPendingOnly", "1");
            copyTaskDefinitionIdsToParams(params, window.location.search);
            if (searchTerm.trim().length > 0) params.append("search", searchTerm.trim());
            params.append("limit", String(reviewPageSize));
            params.append("offset", String((reviewPage - 1) * reviewPageSize));
            const url = `/api/review/pending${params.toString() ? `?${params}` : ""}`;
            return await getJson<PendingResponse>(url);
        },
        refetchInterval: 30000, // Poll every 30 seconds
        placeholderData: (previous, previousQuery) =>
            previousQuery?.queryKey.filter((_, i) => i !== 6).join(':') ===
                pendingQueryKey.filter((_, i) => i !== 6).join(':') ? previous : undefined,
        refetchOnMount: 'always',
        refetchOnWindowFocus: 'always',
        refetchOnReconnect: 'always',
    });

    // Fetch submission detail when one is selected
    const { data: detailData, isLoading: detailLoading } = useQuery<{ success: boolean; data: SubmissionDetail }>({
        queryKey: ["/api/review/submissions", selectedSubmission],
        queryFn: async () => {
            return await getJson<{ success: boolean; data: SubmissionDetail }>(`/api/review/submissions/${selectedSubmission}`);
        },
        enabled: !!selectedSubmission,
    });

    // Fetch reason codes
    const { data: reasonCodesData, isLoading: reasonCodesLoading } = useQuery<{ success: boolean; data: ReasonCode[] }>({
        queryKey: ["/api/review/reason-codes", reviewDecision, selectedRejectionType],
        queryFn: async () => {
            const params = new URLSearchParams();
            if (selectedRejectionType) params.set("rejectionType", selectedRejectionType);
            const url = `/api/review/reason-codes${params.toString() ? `?${params.toString()}` : ""}`;
            return await getJson<{ success: boolean; data: ReasonCode[] }>(url);
        },
        enabled: reviewDecision === "REJECT" && !!selectedRejectionType,
    });

    // Decision mutation (use http() so Authorization header is sent and favourites are persisted)
    const decisionMutation = useMutation({
        mutationFn: async (decision: {
            postingEventId: number;
            decision: "APPROVE" | "REJECT";
            expectedFormatMatch?: ExpectedFormatMatch;
            expectedFormatMatchNotes?: string;
            supportingEvidenceImageIds?: number[];
            feedbackText?: string;
            reasonCodes?: string[];
            rejectionType?: RejectionType;
            rejectionNotes?: string;
            followUpAction?: "CREATE_REPOST" | "ADVISORY_FEEDBACK";
            favouriteImageIds?: number[];
            retailerReady?: boolean;
            adminNotes?: string;
            formatConfirmations?: Array<{
                formatEvidenceId: number;
                formatId: number | null;
                expectedCount: number;
                submittedCount: number;
                prefillStatus: ReviewFormatConfirmationStatus;
                finalStatus: ReviewFormatConfirmationStatus;
            }>;
        }) => {
            const response = await http("/api/review/decisions", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(decision),
            });
            if (!response.ok) {
                let message = "Failed to save decision";
                let errorCode: string | undefined;
                let repostTaskId: number | null = null;
                try {
                    const errorData = await response.json();
                    const formatted = formatReviewDecisionError(errorData, message);
                    message = formatted.message;
                    errorCode = formatted.errorCode;
                    repostTaskId = formatted.repostTaskId;
                } catch {
                    // Keep the fallback message if the error response is not JSON.
                }
                throw new ReviewDecisionError(message, { errorCode, repostTaskId });
            }
            return response.json();
        },
        onSuccess: (response, decision) => {
            toast({ title: "Decision saved", description: "The submission has been processed." });
            if (response?.warnings?.length) {
                const warnings = response.warnings as string[];
                const skippedCount = warnings.find((w) => w.startsWith("FAVOURITES_SOME_INVALID_SKIPPED:"));
                const skipped = skippedCount ? Number(skippedCount.split(":")[1]) : 0;
                if (warnings.includes("FAVOURITES_TASK_CONTEXT_MISSING") || warnings.includes("FAVOURITES_TASK_CYCLE_NULL")) {
                    toast({
                        title: "Favourites skipped",
                        description: "Unable to resolve task context for favourites.",
                        variant: "destructive",
                    });
                } else if (warnings.includes("FAVOURITES_NONE_VALID")) {
                    toast({
                        title: "Favourites skipped",
                        description: "No valid image favourites to save.",
                        variant: "destructive",
                    });
                } else if (skipped > 0) {
                    toast({
                        title: "Favourites saved with skips",
                        description: `${skipped} item${skipped === 1 ? "" : "s"} skipped (not an image or not part of this submission).`,
                    });
                }
            }
            optimisticallyRemoveReviewPendingSubmission(
                queryClient,
                pendingQueryKey,
                decision.postingEventId,
                { includeReviewed },
            );
            scheduleReviewDecisionBackgroundSync(queryClient, decision.postingEventId);
            setFeedback("");
            setAdminNotesInput("");
            setExpectedFormatMatch("MATCH");
            setExpectedFormatMatchNotes("");
            setRejectionNotes("");
            setReviewDecision("APPROVE");
            setSelectedRejectionType(null);
            setSelectedReasons([]);
            setFavouriteImageIds([]);
            setSupportingFormatImageIds([]);
            setCorrectionMode(false);
            setCorrectionValues({});
            if (response?.data?.superseded) {
                void queryClient.refetchQueries({
                    queryKey: ["/api/review/submissions", decision.postingEventId],
                    type: "active",
                });
                if (isReviewPresenceEnabled()) {
                    reviewPresence.notifyDecisionSuccess();
                    void reviewPresence.focusReviewQueue(decision.postingEventId);
                }
                return;
            }
            if (isReviewPresenceEnabled()) {
                reviewPresence.notifyDecisionSuccess({ willCloseSurface: true });
            }
            setSelectedSubmission(null);
        },
        onError: (error: Error) => {
            const repostTaskId = error instanceof ReviewDecisionError ? error.repostTaskId : null;
            toast({
                title: repostTaskId ? `Repost task #${repostTaskId} needs attention` : "Error",
                description: error.message || "Failed to save decision.",
                variant: "destructive",
                duration: repostTaskId ? 10000 : undefined,
                action: repostTaskId ? (
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                            window.location.href = `/tasks/assignments?tab=mobile&assignmentId=${repostTaskId}`;
                        }}
                    >
                        Open assignments
                    </Button>
                ) : undefined,
            });
        },
    });

    const promoteRetailerReadyMutation = useMutation({
        mutationFn: async (decisionId: number) => {
            const response = await http(`/api/review/decisions/${decisionId}/promote-retailer-ready`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
            });
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData?.error || "Failed to promote to retailer-ready");
            }
            return response.json();
        },
        onSuccess: () => {
            toast({ title: "Promoted to Retailer Ready", description: "This submission is now marked as retailer-ready." });
            queryClient.invalidateQueries({ queryKey: ["/api/review/submissions", selectedSubmission] });
            queryClient.invalidateQueries({ queryKey: ["/api/review/pending"] });
            queryClient.invalidateQueries({ queryKey: ["tracker-stores"] });
            queryClient.invalidateQueries({ queryKey: ["campaign-favourite-images"] });
        },
        onError: (error: Error) => {
            toast({ title: "Error", description: error.message, variant: "destructive" });
        },
    });

    const promoteExamplesMutation = useMutation({
        mutationFn: async ({ postingEventId, evidenceImageIds, mediaFormatId }: { postingEventId: number; evidenceImageIds: number[]; mediaFormatId?: number | null }) => {
            const response = await http(`/api/review/submissions/${postingEventId}/promote-examples`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ evidenceImageIds, mediaFormatId, role: "good_example" }),
            });
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || "Failed to promote examples");
            }
            return response.json();
        },
        onSuccess: (result) => {
            toast({
                title: "Examples promoted",
                description: `${result.insertedCount || 0} image${result.insertedCount === 1 ? "" : "s"} added to the expected format library.`,
            });
            queryClient.invalidateQueries({ queryKey: ["/api/review/submissions", selectedSubmission] });
        },
        onError: (error: Error) => {
            toast({ title: "Error", description: error.message || "Failed to promote examples.", variant: "destructive" });
        },
    });

    // Update favourites on an already-approved submission
    const updateFavouritesMutation = useMutation({
        mutationFn: async ({ postingEventId, favouriteImageIds }: { postingEventId: number; favouriteImageIds: number[] }) => {
            const response = await http(`/api/review/submissions/${postingEventId}/favourites`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ favouriteImageIds }),
            });
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || "Failed to update favourites");
            }
            return response.json();
        },
        onSuccess: () => {
            toast({ title: "Favourites updated", description: "Image favourites have been saved." });
            queryClient.invalidateQueries({ queryKey: ["campaign-favourite-images"] });
            queryClient.invalidateQueries({ queryKey: ["/api/review/submissions"] });
        },
        onError: (err: Error) => {
            toast({ title: "Error", description: err.message || "Failed to update favourites.", variant: "destructive" });
        },
    });

    // Single-row correction mutation (used by inline edit on individual format)
    const quantityCorrectionMutation = useMutation({
        mutationFn: async ({ postingEventId, formatEvidenceId, newActualCount }: { postingEventId: number; formatEvidenceId: number; newActualCount: number }) => {
            const response = await fetch(`/api/review/submissions/${postingEventId}/format-evidence/${formatEvidenceId}`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ newActualCount }),
            });
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || "Failed to update quantity");
            }
            return response.json();
        },
        onSuccess: () => {
            toast({ title: "Quantity corrected", description: "The delivered quantity has been updated." });
            queryClient.invalidateQueries({ queryKey: ["/api/review/submissions", selectedSubmission] });
            setEditingFormatId(null);
            setEditValue("");
        },
        onError: (error: Error) => {
            toast({ title: "Error", description: error.message || "Failed to update quantity.", variant: "destructive" });
        },
    });

    // Atomic bulk correction mutation (used by correction mode save)
    const bulkCorrectionMutation = useMutation({
        mutationFn: async ({ postingEventId, corrections }: { postingEventId: number; corrections: Array<{ formatEvidenceId: number; newActualCount: number }> }) => {
            const response = await fetch(`/api/review/submissions/${postingEventId}/bulk-correct`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ corrections, reason: "Admin correction via review modal" }),
            });
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || "Failed to save corrections");
            }
            return response.json();
        },
        onSuccess: () => {
            toast({ title: "Corrections saved", description: "All quantity corrections have been applied." });
            queryClient.invalidateQueries({ queryKey: ["/api/review/submissions", selectedSubmission] });
        },
        onError: (error: Error) => {
            toast({ title: "Error", description: error.message || "Failed to save corrections.", variant: "destructive" });
        },
    });

    // Remove a single evidence image from the submission (admin correction)
    const deleteImageMutation = useMutation({
        mutationFn: async ({ postingEventId, assetId }: { postingEventId: number; assetId: number }) => {
            const response = await http(`/api/review/submissions/${postingEventId}/images/${assetId}`, {
                method: "DELETE",
            });
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || "Failed to retract image");
            }
            return response.json();
        },
        onSuccess: () => {
            toast({ title: "Image removed", description: "The image has been removed from this submission." });
            queryClient.invalidateQueries({ queryKey: ["/api/review/submissions", selectedSubmission] });
            queryClient.invalidateQueries({ queryKey: ["/api/review/pending"] });
            queryClient.invalidateQueries({ queryKey: ["tracker-stores"] });
            queryClient.invalidateQueries({ queryKey: ["campaign-favourite-images"] });
        },
        onError: (error: Error) => {
            toast({ title: "Error", description: error.message || "Failed to retract image.", variant: "destructive" });
        },
    });

    // Add/replace an evidence image on the submission (admin correction)
    const addImageMutation = useMutation({
        mutationFn: async ({ postingEventId, file }: { postingEventId: number; file: File }) => {
            const formData = new FormData();
            formData.append("photo", file);
            const response = await apiFetch(`/api/review/submissions/${postingEventId}/images`, {
                method: "POST",
                body: formData,
            });
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || "Failed to add image");
            }
            return response.json();
        },
        onSuccess: () => {
            toast({ title: "Image added", description: "The new image has been attached to this submission." });
            queryClient.invalidateQueries({ queryKey: ["/api/review/submissions", selectedSubmission] });
            queryClient.invalidateQueries({ queryKey: ["/api/review/pending"] });
            queryClient.invalidateQueries({ queryKey: ["tracker-stores"] });
            queryClient.invalidateQueries({ queryKey: ["campaign-favourite-images"] });
        },
        onError: (error: Error) => {
            toast({ title: "Error", description: error.message || "Failed to add image.", variant: "destructive" });
        },
    });

    const handleStartEdit = (ev: FormatEvidence) => {
        // Only allow editing if there's a real format_evidence record (positive id)
        if (ev.id < 0) {
            toast({
                title: "Cannot edit",
                description: "This format has no delivery record. The operator must submit evidence first.",
                variant: "destructive"
            });
            return;
        }
        setEditingFormatId(ev.id);
        setEditValue(String(ev.actualCount ?? 0));
    };

    const handleSaveEdit = (ev: FormatEvidence) => {
        const newCount = parseInt(editValue);
        if (isNaN(newCount) || newCount < 0) {
            toast({ title: "Invalid value", description: "Please enter a valid non-negative number.", variant: "destructive" });
            return;
        }
        if (!selectedSubmission) return;
        quantityCorrectionMutation.mutate({
            postingEventId: selectedSubmission,
            formatEvidenceId: ev.id,
            newActualCount: newCount,
        });
    };

    const handleCancelEdit = () => {
        setEditingFormatId(null);
        setEditValue("");
    };

    const handleEnterCorrectionMode = () => {
        if (!detail?.formatEvidence) return;
        const initial: Record<number, string> = {};
        for (const ev of detail.formatEvidence) {
            if (ev.id > 0) {
                initial[ev.id] = String(ev.actualCount ?? 0);
            }
        }
        const rows = detail.formatRowConfirmations || [];
        for (const row of rows) {
            if (row.formatEvidenceId > 0 && !(row.formatEvidenceId in initial)) {
                initial[row.formatEvidenceId] = String(row.submittedCount ?? 0);
            }
        }
        setCorrectionValues(initial);
        setCorrectionMode(true);
        setEditingFormatId(null);
    };

    const handleCancelCorrectionMode = () => {
        setCorrectionMode(false);
        setCorrectionValues({});
    };

    const handleSaveCorrectionMode = async () => {
        if (!selectedSubmission) return;
        const originalByFeId = new Map<number, number>();
        for (const ev of (detail?.formatEvidence || [])) {
            if (ev.id > 0) originalByFeId.set(ev.id, ev.actualCount ?? 0);
        }
        for (const row of (detail?.formatRowConfirmations || [])) {
            if (row.formatEvidenceId > 0 && !originalByFeId.has(row.formatEvidenceId)) {
                originalByFeId.set(row.formatEvidenceId, row.submittedCount ?? 0);
            }
        }
        const changes: Array<{ formatEvidenceId: number; newActualCount: number }> = [];
        for (const [feId, valStr] of Object.entries(correctionValues)) {
            const id = Number(feId);
            if (id <= 0) continue;
            const newVal = parseInt(valStr);
            if (isNaN(newVal) || newVal < 0) continue;
            const original = originalByFeId.get(id) ?? 0;
            if (newVal !== original) {
                changes.push({ formatEvidenceId: id, newActualCount: newVal });
            }
        }
        if (changes.length === 0) {
            setCorrectionMode(false);
            setCorrectionValues({});
            return;
        }
        await bulkCorrectionMutation.mutateAsync({
            postingEventId: selectedSubmission,
            corrections: changes,
        });
        setCorrectionMode(false);
        setCorrectionValues({});
    };

    const handleApprove = (retailerReady = false) => {
        if (!selectedSubmission) return;
        decisionMutation.mutate({
            postingEventId: selectedSubmission,
            decision: "APPROVE",
            retailerReady,
            supportingEvidenceImageIds: supportingFormatImageIds,
            feedbackText: feedback.trim() || undefined,
            adminNotes: adminNotesInput.trim() || undefined,
            favouriteImageIds: favouriteImageIds.length > 0 ? favouriteImageIds : undefined,
            formatConfirmations: (detail?.formatRowConfirmations || []).map((row) => ({
                formatEvidenceId: row.formatEvidenceId,
                formatId: row.formatId,
                expectedCount: row.expectedCount,
                submittedCount: row.submittedCount,
                prefillStatus: row.prefillStatus,
                finalStatus: formatConfirmationStates[row.formatEvidenceId] || row.finalStatus || row.prefillStatus,
            })),
        });
    };

    const handleReject = () => {
        if (!selectedSubmission) return;
        if (!selectedRejectionType) {
            toast({ title: "Required action missing", description: "Select what must happen next.", variant: "destructive" });
            return;
        }
        const mustPickConfiguredReason = reasonCodes.length > 0;
        const advisoryNoteOnly =
            selectedRejectionType === "ADVISORY_ONLY"
            && !mustPickConfiguredReason
            && (feedback.trim().length > 0 || rejectionNotes.trim().length > 0);
        if (selectedReasons.length === 0 && !advisoryNoteOnly) {
            toast({
                title: "Rejection reason required",
                description: mustPickConfiguredReason
                    ? "Select at least one rejection reason."
                    : selectedRejectionType === "ADVISORY_ONLY"
                        ? "Select a reason chip or add a note for the operator."
                        : "Select at least one rejection reason.",
                variant: "destructive",
            });
            return;
        }
        const notesRequired = selectedReasons.some((code) => {
            const reason = reasonCodeByCode.get(code);
            return reason?.notesRequirement === "REQUIRED" || code === "OTHER";
        });
        if (notesRequired && !rejectionNotes.trim()) {
            toast({ title: "Notes required", description: "Notes are required for the selected reason(s).", variant: "destructive" });
            return;
        }

        decisionMutation.mutate({
            postingEventId: selectedSubmission,
            decision: selectedRejectionType === "ADVISORY_ONLY" ? "APPROVE" : "REJECT",
            supportingEvidenceImageIds: supportingFormatImageIds,
            feedbackText: feedback.trim() || undefined,
            adminNotes: adminNotesInput.trim() || undefined,
            reasonCodes: selectedReasons,
            rejectionType: selectedRejectionType,
            rejectionNotes: rejectionNotes.trim() || undefined,
            followUpAction: rejectFollowUpActionForType(selectedRejectionType),
            formatConfirmations: (detail?.formatRowConfirmations || []).map((row) => ({
                formatEvidenceId: row.formatEvidenceId,
                formatId: row.formatId,
                expectedCount: row.expectedCount,
                submittedCount: row.submittedCount,
                prefillStatus: row.prefillStatus,
                finalStatus: formatConfirmationStates[row.formatEvidenceId] || row.finalStatus || row.prefillStatus,
            })),
        });
    };

    const handleSubmitDecision = () => {
        if (reviewDecision === "APPROVE") {
            handleApprove(false);
            return;
        }
        if (reviewDecision === "APPROVE_RETAILER_READY") {
            handleApprove(true);
            return;
        }
        handleReject();
    };

    const submissionRows = (pendingData?.data || []).map((row) => ({ ...row, queueKind: "submission" as const }));
    const heldRowsRaw = (pendingData?.held || []).map(mapHeldRowToSubmission);
    const heldPartition = partitionHeldQueueRows(heldRowsRaw);
    const paginationMode: ReviewPendingPaginationMode = typeFilter === "held_upload_pending"
        ? "heldUploadPendingOnly"
        : typeFilter === "held"
            ? "heldOnly"
            : (pendingData?.pagination.mode ?? "combined");
    const heldRows = resolveHeldRowsForQueueView({
        typeFilter,
        paginationMode,
        heldRows: heldRowsRaw,
    });
    const reviewTotals = buildReviewPendingTotals({
        submissionTotal: pendingData?.submissionTotal
            ?? (paginationMode === "combined" ? submissionRows.length : 0),
        heldTotal: pendingData?.heldTotal ?? heldPartition.reviewReady.length,
        heldUploadPendingTotal: pendingData?.heldUploadPendingTotal ?? heldPartition.uploadPending.length,
        heldOnly: paginationMode === "heldOnly",
        heldUploadPendingOnly: paginationMode === "heldUploadPendingOnly",
    });
    const submissions = buildCombinedReviewTableRows({
        paginationMode,
        reviewPage,
        heldRows,
        submissionRows,
    });
    const heldTotal = reviewTotals.heldTotal;
    const heldUploadPendingTotal = reviewTotals.heldUploadPendingTotal;
    const submissionTotal = reviewTotals.submissionTotal;
    const emptyState = describeReviewQueueEmptyMessage({
        typeFilter,
        includeReviewed,
        submissionTotal,
        heldTotal,
    });
    const heldEvidenceBlocked = isHeldEvidenceBlockedByIncludeReviewed(typeFilter, includeReviewed);
    const tablePaginationNote = describeCombinedTablePaginationNote({
        paginationMode,
        reviewPage,
        heldTotal,
        typeFilter,
    });
    const tablePaginationUnit = paginationMode === "heldOnly"
        ? "held photos"
        : paginationMode === "heldUploadPendingOnly"
            ? "uploads pending"
            : "submissions";
    const filteredSubmissions = submissions.filter((sub) => {
        if (typeFilter === "held" || typeFilter === "held_upload_pending") return sub.queueKind === "held";
        if (typeFilter === "removal") return sub.taskType === "REMOVAL";
        if (typeFilter === "posting") return sub.queueKind !== "held" && sub.taskType !== "REMOVAL";
        return true;
    });
    const showQueueEmptyHint = !searchTerm.trim() && filteredSubmissions.length === 0;
    const selectedSubmissionRow = selectedSubmission
        ? submissions.find((row) => row.queueKind !== "held" && row.postingEventId === selectedSubmission) ?? null
        : null;
    const totalPages = resolveReviewPendingPageCount({
        paginationTotal: reviewTotals.paginationTotal,
        pageSize: reviewPageSize,
    });
    useEffect(() => {
        if (reviewPage > totalPages) {
            setReviewPage(totalPages);
        }
    }, [reviewPage, totalPages]);
    const detail = detailData?.data;
    const modalAiVerdict = detail?.aiVerdict ?? selectedSubmissionRow?.aiVerdict ?? null;
    const modalAiVerdictReasons = Array.isArray(detail?.aiVerdictReasons) && detail.aiVerdictReasons.length > 0
        ? detail.aiVerdictReasons
        : (Array.isArray(selectedSubmissionRow?.aiVerdictReasons) ? selectedSubmissionRow.aiVerdictReasons : []);
    const reasonCodes = reasonCodesData?.data || [];

    const userRoles = Array.isArray(user?.roles) ? user.roles : [];
    const isOperatorUser = user?.role === "operator" || userRoles.includes("operator");
    const isReviewerLikeUser = user?.role === "reviewer" || userRoles.includes("reviewer");
    const isAdminLikeUser = user?.role === "admin"
        || userRoles.includes("admin")
        || userRoles.includes("superadmin")
        || userRoles.includes("super_admin");
    const canViewAiTriage = !isOperatorUser;
    const canRunAiTriage = !isOperatorUser && (userRoles.includes("AI_TRIAGE_RUN") || isAdminLikeUser || isReviewerLikeUser);
    const reasonCodeByCode = useMemo(() => new Map(reasonCodes.map((rc) => [rc.code, rc])), [reasonCodes]);
    const notesRequired = selectedReasons.some((code) => {
        const reason = reasonCodeByCode.get(code);
        return reason?.notesRequirement === "REQUIRED" || code === "OTHER";
    });
    const latestDecision = detail?.latestDecision || null;
    const isDecisionLocked = Boolean(latestDecision);
    const [editingAdminNotes, setEditingAdminNotes] = useState(false);
    const [adminNotesText, setAdminNotesText] = useState("");
    const [savingAdminNotes, setSavingAdminNotes] = useState(false);
    const [adminNotesInput, setAdminNotesInput] = useState("");

    const startRejectInstead = () => {
        setReviewDecision("REJECT");
        setSelectedRejectionType(null);
        setSelectedReasons([]);
        setRejectionNotes("");
    };

    useEffect(() => {
        setEditingAdminNotes(false);
        setAdminNotesText(latestDecision?.adminNotes || "");
    }, [latestDecision?.id, latestDecision?.adminNotes]);

    async function saveAdminNotes() {
        if (!latestDecision?.id) return;
        setSavingAdminNotes(true);
        try {
            const res = await http(`/api/review/decisions/${latestDecision.id}/admin-notes`, {
                method: "PATCH",
                body: JSON.stringify({ adminNotes: adminNotesText }),
                headers: { "Content-Type": "application/json" },
            });
            if (!res.ok) throw new Error("Failed to save");
            setEditingAdminNotes(false);
            queryClient.invalidateQueries({ queryKey: ["/api/review/submissions", selectedSubmission] });
        } catch {
            // keep editing open
        } finally {
            setSavingAdminNotes(false);
        }
    }

    const reviewedAtLabel = latestDecision?.createdAt
        ? format(new Date(latestDecision.createdAt), "MMM d, yyyy h:mm a")
        : null;
    const latestReasonLabels = Array.isArray(latestDecision?.rejectionReasonLabelsSnapshot)
        ? latestDecision.rejectionReasonLabelsSnapshot.filter((value) => typeof value === "string" && value.trim().length > 0)
        : [];
    const activeRetailerStandards = detail?.activeRetailerStandards ?? null;
    const hasRetailerStandards = Boolean(activeRetailerStandards);

    // Fallback instructions query for environments that use production API without local backend changes.
    // Uses the same admin posting-instructions/meta endpoints used elsewhere in the app.
    const { data: postingInstructionsFallback } = useQuery<{ text: string | null; imageUrl: string | null }>({
        queryKey: [
            "/api/admin/campaign-cycle-meta/posting-instructions",
            detail?.submission?.campaignId,
            detail?.submission?.cycleId,
        ],
        enabled: !!detail?.submission?.campaignId && !!detail?.submission?.cycleId,
        queryFn: async () => {
            const campaignId = detail!.submission.campaignId;
            const cycleId = detail!.submission.cycleId;

            try {
                const blocksResponse = await getJson<{
                    success: boolean; blocks?: Array<{
                        scopeType?: "campaign" | "group" | "pack";
                        instructionsText?: string | null;
                        instructionsImageUrl?: string | null;
                    }>
                }>(`/api/admin/campaign-cycle-meta/posting-instructions/${campaignId}/${cycleId}`);

                const blocks = blocksResponse?.blocks || [];
                const campaignBlock = blocks.find((b) => b.scopeType === "campaign") || blocks[0];
                if (campaignBlock?.instructionsText || campaignBlock?.instructionsImageUrl) {
                    return {
                        text: campaignBlock.instructionsText || null,
                        imageUrl: campaignBlock.instructionsImageUrl || null,
                    };
                }
            } catch {
                // Fall through to legacy meta endpoint.
            }

            try {
                const metaResponse = await getJson<{
                    success: boolean; meta?: {
                        postingInstructionsText?: string | null;
                        postingInstructionsImageUrl?: string | null;
                    }
                }>(`/api/admin/campaign-cycle-meta/${campaignId}/${cycleId}`);
                return {
                    text: metaResponse?.meta?.postingInstructionsText || null,
                    imageUrl: metaResponse?.meta?.postingInstructionsImageUrl || null,
                };
            } catch {
                return { text: null, imageUrl: null };
            }
        },
    });

    const postingInstructionsText =
        detail?.submission?.postingInstructionsText ||
        postingInstructionsFallback?.text ||
        null;
    const postingInstructionsImageUrl =
        detail?.submission?.postingInstructionsImageUrl ||
        postingInstructionsFallback?.imageUrl ||
        null;

    // Load existing favourites from the detail response, or reset to empty for new submissions
    useEffect(() => {
        const existingFavs = detailData?.data?.favouriteImageIds;
        if (Array.isArray(existingFavs) && existingFavs.length > 0) {
            setFavouriteImageIds(existingFavs);
        } else {
            setFavouriteImageIds([]);
        }
    }, [selectedSubmission, detailData?.data?.submission?.id, detailData?.data?.favouriteImageIds]);

    useEffect(() => {
        const latestDecision = detailData?.data?.latestDecision;
        if (latestDecision?.expectedFormatMatch) {
            setExpectedFormatMatch(latestDecision.expectedFormatMatch);
            setExpectedFormatMatchNotes(latestDecision.expectedFormatMatchNotes || "");
            return;
        }
        setExpectedFormatMatch("MATCH");
        setExpectedFormatMatchNotes("");
    }, [selectedSubmission, detailData?.data?.submission?.id, detailData?.data?.latestDecision?.id, detailData?.data?.latestDecision?.expectedFormatMatch, detailData?.data?.latestDecision?.expectedFormatMatchNotes]);

    useEffect(() => {
        const latestLabel = detailData?.data?.formatReviewLabels?.[0];
        if (Array.isArray(latestLabel?.supportingEvidenceImageIds) && latestLabel.supportingEvidenceImageIds.length > 0) {
            setSupportingFormatImageIds(latestLabel.supportingEvidenceImageIds);
            return;
        }
        setSupportingFormatImageIds([]);
    }, [selectedSubmission, detailData?.data?.submission?.id, detailData?.data?.formatReviewLabels]);

    useEffect(() => {
        const rows = detailData?.data?.formatRowConfirmations || [];
        if (!rows.length) {
            setFormatConfirmationStates({});
            return;
        }
        const nextState: Record<number, ReviewFormatConfirmationStatus> = {};
        rows.forEach((row) => {
            nextState[row.formatEvidenceId] = row.finalStatus || row.prefillStatus;
        });
        setFormatConfirmationStates(nextState);
    }, [selectedSubmission, detailData?.data?.submission?.id, detailData?.data?.formatRowConfirmations]);

    useEffect(() => {
        setSelectedReasons([]);
    }, [selectedRejectionType]);

    useEffect(() => {
        if (reviewDecision === "REJECT") return;
        setSelectedRejectionType(null);
        setSelectedReasons([]);
        setRejectionNotes("");
    }, [reviewDecision]);

    useEffect(() => {
        if (!selectedSubmission) return;
        setFeedback("");
        setAdminNotesInput("");
        setRejectionNotes("");
        setReviewDecision("APPROVE");
        setSelectedRejectionType(null);
        setSelectedReasons([]);
        setFormatConfirmationStates({});
        setReviewModalTab("compliance");
    }, [selectedSubmission]);
    const claimedDeliveryRows = detail?.formatRowConfirmations || [];
    const totalExpectedUnits = claimedDeliveryRows.length > 0
        ? claimedDeliveryRows.reduce((acc, curr) => acc + curr.expectedCount, 0)
        : detail?.formatEvidence.reduce((acc, curr) => acc + curr.expectedCount, 0) || 0;
    const totalReportedUnits = claimedDeliveryRows.length > 0
        ? claimedDeliveryRows.reduce((acc, curr) => acc + curr.submittedCount, 0)
        : detail?.formatEvidence.reduce((acc, curr) => acc + (curr.actualCount ?? 0), 0) || 0;
    const claimedSubmissionTitle = getClaimedSubmissionTitle(detail?.submission.taskType);
    const claimedSubmissionActionLabel = getClaimedSubmissionActionLabel(detail?.submission.taskType);
    const reportedUnitsLabel = getReportedUnitsLabel(detail?.submission.taskType);
    const reportedCountBadgeLabel = getReportedCountBadgeLabel(detail?.submission.taskType);
    const formatConfirmationSubjectLabel = getFormatConfirmationSubjectLabel(detail?.submission.taskType);
    const quantityLabel = getQuantityLabel(detail?.submission.taskType);
    const expectedFormatContexts = (() => {
        const apiContexts = detail?.expectedFormatContexts || [];
        if (apiContexts.length > 0) {
            return apiContexts;
        }

        const fallbackFormats = detail?.formatEvidence || [];
        if (fallbackFormats.length > 0) {
            return fallbackFormats.map((format) => {
                const primaryContext = detail?.formatContext;
                const matchesPrimaryContext = primaryContext
                    && (
                        (primaryContext.mediaFormatId != null && primaryContext.mediaFormatId === format.formatId)
                        || (
                            primaryContext.expectedFormatName != null
                            && primaryContext.expectedFormatName.trim().toLowerCase() === format.formatName.trim().toLowerCase()
                        )
                    );

                return {
                    mediaFormatId: format.formatId ?? null,
                    expectedFormatName: format.formatName,
                    expectedQuantity: Math.max(0, Number(format.expectedCount || 0)),
                    reviewerDescription: matchesPrimaryContext ? primaryContext.reviewerDescription ?? null : null,
                    typicalDimensions: matchesPrimaryContext ? primaryContext.typicalDimensions ?? null : null,
                    placementGuidance: matchesPrimaryContext ? primaryContext.placementGuidance ?? null : null,
                    visualCues: matchesPrimaryContext ? primaryContext.visualCues || [] : [],
                    reviewChecklist: matchesPrimaryContext ? primaryContext.reviewChecklist || [] : [],
                    exampleImages: matchesPrimaryContext ? primaryContext.exampleImages || [] : [],
                };
            });
        }

        const primaryContext = detail?.formatContext;
        if (!primaryContext?.expectedFormatName) {
            return [];
        }

        return [{
            mediaFormatId: primaryContext.mediaFormatId ?? null,
            expectedFormatName: primaryContext.expectedFormatName,
            expectedQuantity: Math.max(0, Number(primaryContext.expectedQuantity || 0)),
            reviewerDescription: primaryContext.reviewerDescription ?? null,
            typicalDimensions: primaryContext.typicalDimensions ?? null,
            placementGuidance: primaryContext.placementGuidance ?? null,
            visualCues: primaryContext.visualCues || [],
            reviewChecklist: primaryContext.reviewChecklist || [],
            exampleImages: primaryContext.exampleImages || [],
        }];
    })();
    const trainingTargetFormat = expectedFormatContexts.find((format) => String(format.mediaFormatId) === selectedTrainingFormatId) || null;
    const aiFeedbackStatusLabel = getAiFeedbackStatusLabel(
        expectedFormatContexts.length || claimedDeliveryRows.length,
        detail?.formatReviewLabels?.length || 0,
    );

    useEffect(() => {
        if (expectedFormatContexts.length === 0) {
            setSelectedTrainingFormatId("");
            return;
        }
        const firstSelectable = expectedFormatContexts.find((format) => format.mediaFormatId != null);
        setSelectedTrainingFormatId((prev) => {
            if (prev && expectedFormatContexts.some((format) => String(format.mediaFormatId) === prev)) {
                return prev;
            }
            return firstSelectable?.mediaFormatId != null ? String(firstSelectable.mediaFormatId) : "";
        });
    }, [detail?.submission.id, expectedFormatContexts]);
    const columns = useMemo<ColumnDef<Submission>[]>(() => [
        {
            accessorKey: "storeName",
            header: ({ column }) => (
                <Button
                    variant="ghost"
                    className="px-0"
                    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                    Store
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            ),
            cell: ({ row }) => <span className="font-medium">{row.original.storeName}</span>,
        },
        {
            accessorKey: "campaignName",
            header: ({ column }) => (
                <Button
                    variant="ghost"
                    className="px-0"
                    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                    Campaign
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            ),
            cell: ({ row }) => (
                <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-2">
                        {row.original.queueKind === "held" ? (
                            <ShieldCheck className="h-3.5 w-3.5 text-teal-600" />
                        ) : row.original.taskType === "REMOVAL" ? (
                            <Trash2 className="h-3.5 w-3.5 text-orange-500" />
                        ) : (
                            <Package className="h-3.5 w-3.5 text-blue-500" />
                        )}
                        <span className="text-sm font-medium text-foreground">
                            {row.original.campaignName.replace(" (Removal)", "")}
                        </span>
                    </div>
                    {row.original.assignmentId && (
                        <span className="text-xs text-muted-foreground ml-5">
                            #{row.original.assignmentId}
                        </span>
                    )}
                </div>
            ),
        },
        {
            accessorKey: "taskType",
            header: ({ column }) => (
                <Button
                    variant="ghost"
                    className="px-0"
                    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                    Type
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            ),
            cell: ({ row }) => (
                <span className="text-muted-foreground">
                    {row.original.queueKind === "held"
                        ? heldEvidenceQueueTypeLabel(row.original.heldStatus)
                        : row.original.taskType === "REMOVAL"
                            ? "Removal"
                            : "Posting"}
                </span>
            ),
        },
        {
            id: "status",
            header: ({ column }) => (
                <Button
                    variant="ghost"
                    className="px-0"
                    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                    Status
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            ),
            cell: ({ row }) => (
                <span className="text-muted-foreground">
                    {row.original.queueKind === "held" && row.original.heldStatus
                        ? heldEvidenceReviewStatusLabel(row.original.heldStatus)
                        : row.original.latestDecision === "APPROVE"
                            ? "Approved"
                            : row.original.latestDecision === "REJECT"
                                ? "Rejected"
                                : "To be reviewed"}
                </span>
            ),
        },
        {
            accessorKey: "aiVerdict",
            header: ({ column }) => (
                <Button
                    variant="ghost"
                    className="px-0"
                    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                    AI Triage
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            ),
            cell: ({ row }) => {
                if (row.original.queueKind === "held") {
                    return <span className="text-xs text-muted-foreground">N/A</span>;
                }
                const verdict = row.original.aiVerdict ?? null;
                const reasons = Array.isArray(row.original.aiVerdictReasons)
                    ? row.original.aiVerdictReasons.filter((reason) => typeof reason === "string" && reason.trim().length > 0)
                    : [];
                if (!verdict) {
                    return <span className="text-xs text-muted-foreground">Not triaged yet</span>;
                }

                if (verdict === "PASS") {
                    return <Badge className="bg-emerald-100 text-emerald-900 border border-emerald-200 hover:bg-emerald-100">Pass</Badge>;
                }
                if (verdict === "PARTIAL_FAIL") {
                    const partialBadge = <Badge className="bg-amber-100 text-amber-900 border border-amber-200 hover:bg-amber-100">Partial</Badge>;
                    if (reasons.length === 0) {
                        return partialBadge;
                    }
                    return (
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <span className="inline-flex cursor-help">{partialBadge}</span>
                                </TooltipTrigger>
                                <TooltipContent>
                                    <p className="max-w-xs text-xs leading-relaxed">{reasons[0]}</p>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    );
                }
                const failBadge = <Badge className="bg-red-100 text-red-900 border border-red-200 hover:bg-red-100">Fail</Badge>;
                if (reasons.length === 0) {
                    return failBadge;
                }
                return (
                    <TooltipProvider>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span className="inline-flex cursor-help">{failBadge}</span>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p className="max-w-xs text-xs leading-relaxed">{reasons[0]}</p>
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                );
            },
        },
        {
            id: "discrepancy",
            header: ({ column }) => (
                <Button
                    variant="ghost"
                    className="px-0"
                    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                    Discrepancy
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            ),
            cell: ({ row }) => (
                <span className="text-muted-foreground">
                    {row.original.hasDiscrepancy ? "Y" : "N"}
                </span>
            ),
        },
        {
            accessorKey: "operatorName",
            header: ({ column }) => (
                <Button
                    variant="ghost"
                    className="px-0"
                    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                    Operator
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            ),
            cell: ({ row }) => <span className="text-muted-foreground">{row.original.operatorName}</span>,
        },
        {
            accessorKey: "imageCount",
            header: () => <div className="text-center">Images</div>,
            cell: ({ row }) => (
                <div className="text-center">
                    <Badge variant="secondary">
                        <ImageIcon className="h-3 w-3 mr-1" />
                        {row.original.imageCount}
                    </Badge>
                </div>
            ),
        },
        {
            accessorKey: "timestamp",
            header: ({ column }) => (
                <div className="text-right">
                    <Button
                        variant="ghost"
                        className="px-0"
                        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                    >
                        Submitted
                        <ArrowUpDown className="ml-2 h-4 w-4" />
                    </Button>
                </div>
            ),
            cell: ({ row }) => (
                <div className="text-right text-sm text-muted-foreground">
                    {formatDistanceToNow(new Date(row.original.timestamp), { addSuffix: true })}
                </div>
            ),
        },
        {
            id: "actions",
            header: "",
            cell: ({ row }) => (
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                        if (row.original.queueKind === "held" && row.original.mediaOperationId) {
                            setSelectedHeldMediaOperationId(row.original.mediaOperationId);
                            return;
                        }
                        setSelectedSubmission(row.original.postingEventId);
                    }}
                >
                    <Eye className="h-4 w-4 mr-1" />
                    Review
                </Button>
            ),
        },
    ], []);

    return (
        <TrackerLayout
            title="Review & Approve"
            onRefresh={() => refetch()}
            isRefreshing={isRefetching}
        >
            <div className="space-y-6 max-w-7xl mx-auto">
                {storeScope.scoped && <div className="text-sm text-muted-foreground" role="status">
                    Reviewing {storeScope.storeName || `store ${storeId}`} · {includeReviewed ? 'Including reviewed' : 'Pending jobs'}
                    {typeFilter === 'all' ? ' · Posting, removal and repost' : ''}
                    {campaignId && campaignId !== 'all' ? ` · Campaign ${campaignId}` : ''}
                </div>}
                {error && (
                    <Card className="border-destructive">
                        <CardContent className="p-6 text-center text-destructive">
                            Failed to load submissions. Please try again.
                        </CardContent>
                    </Card>
                )}

                {isLoading && (
                    <div className="flex justify-center py-12">
                        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                    </div>
                )}

                {!isLoading && !error && (
                    <Card>
                        <CardHeader className="gap-4 space-y-0 pb-4">
                            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                                <div className="space-y-3">
                                    <CardTitle>Review queue</CardTitle>
                                    <div className="flex flex-wrap items-center gap-2">
                                        <ReviewQueueStatBadge
                                            icon={Clock}
                                            count={submissionTotal}
                                            label={includeReviewed ? "total" : "pending"}
                                            active={typeFilter !== "held" && typeFilter !== "held_upload_pending"}
                                            tone="default"
                                        />
                                        {!storeScope.scoped && <ReviewQueueStatBadge
                                            icon={ShieldCheck}
                                            count={heldTotal}
                                            label="ready to review"
                                            active={typeFilter === "held"}
                                            tone="held"
                                        />}
                                        {!storeScope.scoped && <ReviewQueueStatBadge
                                            icon={Upload}
                                            count={heldUploadPendingTotal}
                                            label="waiting for upload"
                                            active={typeFilter === "held_upload_pending"}
                                            tone="held"
                                        />}
                                        {!cycleId && (
                                            <Badge variant="outline" className="font-normal text-muted-foreground">
                                                Select a cycle to filter
                                            </Badge>
                                        )}
                                    </div>
                                </div>
                                <ReviewQueueToolbar
                                    includeReviewed={includeReviewed}
                                    onIncludeReviewedChange={setIncludeReviewed}
                                    typeFilter={typeFilter}
                                    ordinaryOnly={storeScope.scoped}
                                    onTypeFilterChange={setTypeFilter}
                                />
                            </div>
                            {heldEvidenceBlocked ? (
                                <p className="text-sm text-muted-foreground">
                                    Held photos are hidden while Include reviewed is on.
                                </p>
                            ) : null}
                            {includeReviewed && !heldEvidenceBlocked ? (
                                <p className="text-sm text-muted-foreground">
                                    Showing reviewed submissions as well as pending items.
                                </p>
                            ) : null}
                        </CardHeader>
                        <CardContent className="space-y-4">
                            {showQueueEmptyHint ? (
                                <div className="flex flex-col gap-3 rounded-lg border border-dashed px-4 py-5 text-sm sm:flex-row sm:items-center sm:justify-between">
                                    <p className="text-muted-foreground">{emptyState.detail}</p>
                                    {emptyState.showViewSubmissionsAction && submissionTotal > 0 ? (
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="shrink-0"
                                            onClick={() => setTypeFilter("all")}
                                        >
                                            View {submissionTotal} submissions
                                        </Button>
                                    ) : null}
                                </div>
                            ) : null}
                            <DataTable
                                columns={columns}
                                data={filteredSubmissions}
                                searchPlaceholder="Search campaigns..."
                                serverSearch={{
                                    value: searchTerm,
                                    onChange: setSearchTerm,
                                    placeholder: "Search campaigns, stores, operators...",
                                }}
                                showColumnToggle={false}
                                isLoading={isLoading}
                                serverPagination={{
                                    currentPage: reviewPage,
                                    totalPages,
                                    totalCount: reviewTotals.paginationTotal,
                                    totalCountUnit: tablePaginationUnit,
                                    paginationNote: tablePaginationNote,
                                    pageSize: reviewPageSize,
                                    onPageChange: (page) => setReviewPage(Math.max(1, page)),
                                    onPageSizeChange: (pageSize) => {
                                        setReviewPageSize(Math.min(50, Math.max(10, pageSize)));
                                        setReviewPage(1);
                                    },
                                    pageSizeOptions: [10, 20, 30, 50],
                                }}
                            />
                        </CardContent>
                    </Card>
                )}
            </div>

            {/* Review Modal */}
            <Dialog open={!!selectedSubmission} onOpenChange={(open) => !open && setSelectedSubmission(null)}>
                <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Review Submission</DialogTitle>
                    </DialogHeader>

                    {detailLoading ? (
                        <div className="flex justify-center py-12">
                            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                        </div>
                    ) : detail ? (
                        <div className="space-y-6">
                            <Card>
                                <CardContent className="pt-5">
                                    <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                                        <div className="space-y-3">
                                            <div>
                                                <h2 className="text-xl font-semibold leading-tight">{detail.submission.storeName}</h2>
                                                <p className="text-xs text-muted-foreground">Submission #{detail.submission.id}</p>
                                            </div>
                                            <div className="flex flex-wrap gap-2">
                                                <Badge variant="outline">
                                                    {detail.submission.campaignName.replace(" (Removal)", "")}
                                                </Badge>
                                                {detail.submission.campaignName.includes("(Removal)") && (
                                                    <Badge className="bg-orange-500 hover:bg-orange-600 text-[10px] h-5 px-2 uppercase font-bold tracking-wider">
                                                        Removal
                                                    </Badge>
                                                )}
                                                <Badge variant="secondary">{detail.submission.postedUnits} units</Badge>
                                                <Badge variant="outline">
                                                    Compliance {latestDecision?.decision === "APPROVE" ? "approved" : latestDecision?.decision === "REJECT" ? "rejected" : "pending"}
                                                </Badge>
                                                <Badge variant="outline">
                                                    AI feedback {aiFeedbackStatusLabel.toLowerCase()}
                                                </Badge>
                                                {(() => {
                                                    const totalExpected = detail.formatEvidence.reduce((acc, curr) => acc + curr.expectedCount, 0);
                                                    const totalActual = detail.formatEvidence.reduce((acc, curr) => acc + (curr.actualCount ?? 0), 0);
                                                    if (detail.formatEvidence.length > 0 && totalExpected !== totalActual) {
                                                        return (
                                                            <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">
                                                                Partial
                                                            </Badge>
                                                        );
                                                    }
                                                    return null;
                                                })()}
                                            </div>
                                        </div>

                                        <div className="grid gap-2 text-sm text-muted-foreground xl:text-right">
                                            <div className="flex items-center gap-2 xl:justify-end">
                                                <User className="h-4 w-4" />
                                                <span>{detail.submission.operatorName}</span>
                                            </div>
                                            <div className="flex items-center gap-2 xl:justify-end">
                                                <Calendar className="h-4 w-4" />
                                                <span>{format(new Date(detail.submission.timestamp), "MMM d, yyyy h:mm a")}</span>
                                            </div>
                                            {detail.submission.storeAddress && (
                                                <div className="flex items-center gap-2 xl:justify-end">
                                                    <MapPin className="h-4 w-4" />
                                                    <span>{detail.submission.storeAddress}</span>
                                                </div>
                                            )}
                                            <div className="text-xs">
                                                Pack: {detail.submission.packName || "Standard Pack"}
                                                {detail.submission.assignmentId ? ` • #${detail.submission.assignmentId}` : ""}
                                            </div>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            <InstallExceptionPanel installException={detail.installException} />

                            {latestDecision && (
                                <Card className={latestDecision.decision === "REJECT" ? "border-red-200 bg-red-50/30" : "border-green-200 bg-green-50/30"}>
                                    <CardContent className="pt-4 space-y-2">
                                        <div className="flex flex-wrap items-center justify-between gap-2">
                                            <div className="flex items-center gap-2">
                                                {latestDecision.decision === "APPROVE" ? (
                                                    <CheckCircle className="h-4 w-4 text-green-700" />
                                                ) : (
                                                    <XCircle className="h-4 w-4 text-red-700" />
                                                )}
                                                <span className="text-sm font-medium">
                                                    Reviewed: {latestDecision.rejectionType === "ADVISORY_ONLY" ? "Accepted with concerns" : latestDecision.decision === "APPROVE" ? "Approved" : "Rejected"}
                                                </span>
                                            </div>
                                            <span className="text-xs text-muted-foreground">
                                                {latestDecision.decidedByName || `User #${latestDecision.decidedByUserId}`}
                                                {reviewedAtLabel ? ` • ${reviewedAtLabel}` : ""}
                                            </span>
                                        </div>
                                        {(latestDecision.decision === "REJECT" || latestDecision.rejectionType === "ADVISORY_ONLY") && (
                                            <div className="space-y-1 text-xs text-muted-foreground">
                                                {latestDecision.rejectionType && (
                                                    <p>
                                                        <span className="font-medium text-foreground">Required action:</span>{" "}
                                                        {getRejectionTypeLabel(latestDecision.rejectionType)}
                                                    </p>
                                                )}
                                                {(latestReasonLabels.length > 0 || (latestDecision.reasonCodes?.length || 0) > 0) && (
                                                    <p>
                                                        <span className="font-medium text-foreground">Reasons:</span>{" "}
                                                        {(latestReasonLabels.length > 0 ? latestReasonLabels : (latestDecision.reasonCodes || [])).join(", ")}
                                                    </p>
                                                )}
                                                {latestDecision.rejectionNotes && (
                                                    <p>
                                                        <span className="font-medium text-foreground">Notes:</span>{" "}
                                                        {latestDecision.rejectionNotes}
                                                    </p>
                                                )}
                                            </div>
                                        )}
                                        <p className="text-[11px] text-muted-foreground">
                                            Verdict is locked after review.
                                        </p>
                                        <div className="border-t pt-2 mt-2">
                                            <div className="flex items-center justify-between">
                                                <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Admin Notes</span>
                                                {!editingAdminNotes && (
                                                    <button
                                                        type="button"
                                                        className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-0.5"
                                                        onClick={() => { setAdminNotesText(latestDecision?.adminNotes || ""); setEditingAdminNotes(true); }}
                                                    >
                                                        <Pencil className="h-3 w-3" /> Edit
                                                    </button>
                                                )}
                                            </div>
                                            {editingAdminNotes ? (
                                                <div className="mt-1 space-y-1.5">
                                                    <Textarea
                                                        value={adminNotesText}
                                                        onChange={e => setAdminNotesText(e.target.value)}
                                                        placeholder="Internal note — e.g. why this was approved…"
                                                        className="h-20 text-xs resize-none"
                                                    />
                                                    <div className="flex gap-1.5">
                                                        <Button size="sm" className="h-6 px-2 text-xs" disabled={savingAdminNotes} onClick={saveAdminNotes}>
                                                            {savingAdminNotes ? "Saving…" : "Save"}
                                                        </Button>
                                                        <Button size="sm" variant="outline" className="h-6 px-2 text-xs" disabled={savingAdminNotes} onClick={() => setEditingAdminNotes(false)}>
                                                            Cancel
                                                        </Button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="mt-1 rounded bg-muted/60 px-2 py-1.5 text-xs text-foreground">
                                                    {latestDecision?.adminNotes?.trim() || "No admin notes yet"}
                                                </div>
                                            )}
                                        </div>
                                    </CardContent>
                                </Card>
                            )}

                            <Tabs value={reviewModalTab} onValueChange={(value) => setReviewModalTab(value as "compliance" | "ai-feedback")} className="space-y-4">
                                <TabsList className="grid w-full grid-cols-2">
                                    <TabsTrigger value="compliance">Compliance Review</TabsTrigger>
                                    <TabsTrigger value="ai-feedback">AI Feedback</TabsTrigger>
                                </TabsList>

                                <TabsContent value="compliance" className="space-y-6">
                            <div className="grid gap-4 xl:grid-cols-[1.45fr_0.95fr]">
                                <div className="space-y-4">
                                {showsSubmissionReferencePanel(detail.submission.taskType) && countReferenceKindGroups(detail.sourcePostingReferences) > 0 ? (
                                    <RemovalSourcePostingReferences
                                        references={detail.sourcePostingReferences ?? []}
                                    />
                                ) : null}
                                <CurrentSubmissionEvidencePanel
                                    taskType={detail.submission.taskType}
                                    attachedCount={detail.images.length}
                                    referenceCount={countReferenceKindGroups(detail.sourcePostingReferences)}
                                >
                                        {detail.images.length > 0 && detail.images[0] ? (
                                            <div className="space-y-3">
                                                <a
                                                    href={detail.images[0].secureUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="group block overflow-hidden rounded-xl border bg-muted/10"
                                                >
                                                    <img
                                                        src={detail.images[0].secureUrl}
                                                        alt="Current submission evidence under review"
                                                        className="h-[420px] w-full object-cover transition-transform group-hover:scale-[1.01]"
                                                    />
                                                </a>

                                                {detail.images.length > 1 ? (
                                                    <div className="grid grid-cols-4 gap-3">
                                                        {detail.images.slice(1, 5).map((img) => (
                                                            <a
                                                                key={img.id}
                                                                href={img.secureUrl}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="group overflow-hidden rounded-lg border bg-muted/10"
                                                            >
                                                                <img
                                                                    src={img.secureUrl}
                                                                    alt="Additional current submission evidence"
                                                                    className="h-24 w-full object-cover transition-transform group-hover:scale-[1.02]"
                                                                />
                                                            </a>
                                                        ))}
                                                    </div>
                                                ) : null}
                                            </div>
                                        ) : (
                                            <div className="flex h-40 items-center justify-center rounded-lg border border-dashed text-sm text-muted-foreground">
                                                No photos attached.
                                            </div>
                                        )}
                                </CurrentSubmissionEvidencePanel>
                                </div>

                                <div className="space-y-4">
                                    <Card>
                                        <CardHeader className="pb-3">
                                            <CardTitle className="text-base">Review Decision</CardTitle>
                                            {showsSubmissionReferencePanel(detail.submission.taskType) && countReferenceKindGroups(detail.sourcePostingReferences) > 0 ? (
                                                <p className="text-xs leading-snug text-sky-800">
                                                    {getReviewDecisionScopeHint(detail.submission.taskType, detail.sourcePostingReferences ?? [])}
                                                </p>
                                            ) : null}
                                        </CardHeader>
                                        <CardContent className="space-y-4">
                                            {isDecisionLocked ? (
                                                <div className="space-y-3">
                                                    <div className="flex items-center justify-between gap-3 rounded-md border bg-muted/30 p-3">
                                                        <div>
                                                            <p className="text-sm font-medium">
                                                                {latestDecision?.decision === "APPROVE"
                                                                    ? (latestDecision.retailerReady ? "Approved & Retailer Ready" : "Approved")
                                                                    : "Rejected"}
                                                            </p>
                                                            <p className="text-xs text-muted-foreground">
                                                                This review is locked.
                                                            </p>
                                                        </div>
                                                        <div className="flex items-center gap-1.5">
                                                            {latestDecision?.decision === "APPROVE" && latestDecision.retailerReady && (
                                                                <Badge variant="secondary" className="bg-blue-100 text-blue-700 border-blue-200">
                                                                    <ShieldCheck className="h-3 w-3 mr-1" />
                                                                    Retailer Ready
                                                                </Badge>
                                                            )}
                                                            <Badge variant={latestDecision?.decision === "REJECT" ? "destructive" : "secondary"}>
                                                                {latestDecision?.decision || "REVIEWED"}
                                                            </Badge>
                                                        </div>
                                                    </div>
                                                    <div className="grid grid-cols-2 gap-2">
                                                        <Button
                                                            type="button"
                                                            variant={latestDecision?.decision === "REJECT" ? "destructive" : reviewDecision === "REJECT" ? "destructive" : "outline"}
                                                            disabled={latestDecision?.decision === "REJECT" || decisionMutation.isPending}
                                                            onClick={startRejectInstead}
                                                        >
                                                            <XCircle className="h-4 w-4 mr-2" />
                                                            {latestDecision?.decision === "APPROVE" ? "Reject instead" : "Rejected"}
                                                        </Button>
                                                        <Button
                                                            type="button"
                                                            variant={latestDecision?.decision === "APPROVE" && !latestDecision.retailerReady ? "default" : "outline"}
                                                            className={latestDecision?.decision === "APPROVE" && !latestDecision.retailerReady ? "bg-green-600 hover:bg-green-700" : ""}
                                                            disabled={latestDecision?.decision === "APPROVE" || decisionMutation.isPending}
                                                            onClick={() => setApproveInsteadOpen(true)}
                                                        >
                                                            <CheckCircle className="h-4 w-4 mr-2" />
                                                            {latestDecision?.decision === "REJECT" ? "Approve instead" : "Approved"}
                                                        </Button>
                                                    </div>
                                                    <AlertDialog open={approveInsteadOpen} onOpenChange={setApproveInsteadOpen}>
                                                        <AlertDialogModalContent>
                                                            <AlertDialogHeader>
                                                                <AlertDialogTitle>Approve this rejected submission?</AlertDialogTitle>
                                                                <AlertDialogDescription>
                                                                    This will supersede the previous rejection and recall the repost task sent to the operator. If the operator has already started or submitted the repost, this change will be blocked.
                                                                </AlertDialogDescription>
                                                            </AlertDialogHeader>
                                                            <AlertDialogFooter>
                                                                <AlertDialogCancel disabled={decisionMutation.isPending}>Cancel</AlertDialogCancel>
                                                                <AlertDialogAction
                                                                    disabled={decisionMutation.isPending}
                                                                    onClick={() => {
                                                                        setApproveInsteadOpen(false);
                                                                        handleApprove(false);
                                                                    }}
                                                                >
                                                                    Continue and approve
                                                                </AlertDialogAction>
                                                            </AlertDialogFooter>
                                                        </AlertDialogModalContent>
                                                    </AlertDialog>
                                                    {latestDecision?.decision === "APPROVE" && reviewDecision === "REJECT" && (
                                                        <div className="space-y-4 rounded-md border border-red-200 bg-red-50 p-3">
                                                            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 flex items-start gap-2">
                                                                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                                                                <p>
                                                                    This will supersede the previous approval. The submission will no longer count as approved evidence, and a repost task will be created for the operator.
                                                                </p>
                                                            </div>
                                                            <div>
                                                                <p className="text-sm font-medium mb-2">Required action</p>
                                                                <Select
                                                                    value={selectedRejectionType || ""}
                                                                    onValueChange={(value) => setSelectedRejectionType(value as RejectionType)}
                                                                >
                                                                    <SelectTrigger>
                                                                        <SelectValue placeholder="Select required action" />
                                                                    </SelectTrigger>
                                                                    <SelectContent>
                                                                        <RejectionTypeSelectItems />
                                                                    </SelectContent>
                                                                </Select>
                                                            </div>

                                                            {selectedRejectionType && (
                                                                <div>
                                                                    <p className="text-sm font-medium mb-2">Reasons</p>
                                                                    {reasonCodesLoading ? (
                                                                        <p className="text-sm text-muted-foreground">Loading reasons...</p>
                                                                    ) : reasonCodes.length === 0 ? (
                                                                        <p className="text-sm text-muted-foreground">No active reasons configured for this action.</p>
                                                                    ) : (
                                                                        <div className="flex flex-wrap gap-2">
                                                                            {reasonCodes.map((rc) => (
                                                                                <Badge
                                                                                    key={rc.code}
                                                                                    variant={selectedReasons.includes(rc.code) ? "default" : "outline"}
                                                                                    className={`cursor-pointer ${selectedReasons.includes(rc.code) ? "ring-2 ring-primary/40" : ""}`}
                                                                                    onClick={() => {
                                                                                        setSelectedReasons((prev) => (
                                                                                            prev.includes(rc.code)
                                                                                                ? prev.filter((c) => c !== rc.code)
                                                                                                : [...prev, rc.code]
                                                                                        ));
                                                                                    }}
                                                                                >
                                                                                    {selectedReasons.includes(rc.code) && <CheckCircle2 className="h-3 w-3 mr-1" />}
                                                                                    {rc.label}
                                                                                </Badge>
                                                                            ))}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}

                                                            <div>
                                                                <p className="text-sm font-medium mb-2">
                                                                    Rejection notes {notesRequired ? "(required)" : "(optional)"}
                                                                </p>
                                                                <Textarea
                                                                    placeholder={notesRequired ? "Explain the issue for the operator..." : "Add additional context (optional)..."}
                                                                    value={rejectionNotes}
                                                                    onChange={(e) => setRejectionNotes(e.target.value)}
                                                                    rows={3}
                                                                />
                                                            </div>

                                                            <div>
                                                                <p className="text-sm font-medium mb-2">Operator feedback (optional)</p>
                                                                <Textarea
                                                                    placeholder="Optional message shown to operator..."
                                                                    value={feedback}
                                                                    onChange={(e) => setFeedback(e.target.value)}
                                                                    rows={3}
                                                                />
                                                            </div>

                                                            <Button
                                                                type="button"
                                                                variant="destructive"
                                                                className="w-full"
                                                                disabled={decisionMutation.isPending}
                                                                onClick={handleReject}
                                                            >
                                                                {decisionMutation.isPending ? (
                                                                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                                                ) : (
                                                                    <XCircle className="h-4 w-4 mr-2" />
                                                                )}
                                                                Confirm rejection
                                                            </Button>
                                                        </div>
                                                    )}
                                                    {hasRetailerStandards && latestDecision?.decision === "APPROVE" && !latestDecision.retailerReady && (
                                                        <Button
                                                            type="button"
                                                            variant="outline"
                                                            className="w-full text-blue-700 border-blue-200 hover:bg-blue-50"
                                                            disabled={promoteRetailerReadyMutation.isPending}
                                                            onClick={() => promoteRetailerReadyMutation.mutate(latestDecision.id)}
                                                        >
                                                            <ShieldCheck className="h-4 w-4 mr-2" />
                                                            {promoteRetailerReadyMutation.isPending ? "Promoting..." : "Mark as Retailer Ready"}
                                                        </Button>
                                                    )}
                                                    {hasRetailerStandards && latestDecision?.decision === "APPROVE" && latestDecision.retailerReady && (
                                                        <Button
                                                            type="button"
                                                            variant="default"
                                                            className="w-full bg-blue-600 hover:bg-blue-700"
                                                            disabled
                                                        >
                                                            <ShieldCheck className="h-4 w-4 mr-2" />
                                                            Approve &amp; Retailer Ready
                                                        </Button>
                                                    )}
                                                </div>
                                            ) : (
                                                <>
                                                    <div>
                                                        <div className="flex items-center justify-between mb-2">
                                                            <p className="text-sm font-medium">Decision</p>
                                                            {hasRetailerStandards && (
                                                                <button
                                                                    type="button"
                                                                    className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
                                                                    onClick={() => setGuidanceOpen(o => !o)}
                                                                >
                                                                    Latest Guidance
                                                                </button>
                                                            )}
                                                        </div>
                                                        {guidanceOpen && hasRetailerStandards && (
                                                            <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-xs mb-2 max-h-48 overflow-y-auto">
                                                                <p className="font-medium text-blue-900 mb-1">Image Quality for Retailer Review</p>
                                                                <p className="whitespace-pre-wrap">{activeRetailerStandards!.imageQualityForReview}</p>
                                                            </div>
                                                        )}
                                                        <div className="grid grid-cols-2 gap-2">
                                                            <Button
                                                                type="button"
                                                                variant={reviewDecision === "REJECT" ? "destructive" : "outline"}
                                                                onClick={() => setReviewDecision("REJECT")}
                                                                disabled={decisionMutation.isPending}
                                                            >
                                                                <XCircle className="h-4 w-4 mr-2" />
                                                                Reject
                                                            </Button>
                                                            <Button
                                                                type="button"
                                                                variant={reviewDecision === "APPROVE" ? "default" : "outline"}
                                                                className={reviewDecision === "APPROVE" ? "bg-green-600 hover:bg-green-700" : ""}
                                                                onClick={() => setReviewDecision("APPROVE")}
                                                                disabled={decisionMutation.isPending}
                                                            >
                                                                <CheckCircle className="h-4 w-4 mr-2" />
                                                                Approve
                                                            </Button>
                                                        </div>
                                                        {hasRetailerStandards && (
                                                            <Button
                                                                type="button"
                                                                variant={reviewDecision === "APPROVE_RETAILER_READY" ? "default" : "outline"}
                                                                className={`w-full mt-2 ${reviewDecision === "APPROVE_RETAILER_READY" ? "bg-blue-600 hover:bg-blue-700" : "text-blue-700 border-blue-200 hover:bg-blue-50"}`}
                                                                onClick={() => setReviewDecision("APPROVE_RETAILER_READY")}
                                                                disabled={decisionMutation.isPending}
                                                            >
                                                                <ShieldCheck className="h-4 w-4 mr-2" />
                                                                Approve &amp; Retailer Ready
                                                            </Button>
                                                        )}
                                                        {!correctionMode && detail?.formatEvidence && detail.formatEvidence.some(ev => ev.id > 0) && (
                                                            <Button
                                                                type="button"
                                                                variant="outline"
                                                                size="sm"
                                                                className="w-full mt-2 text-blue-700 border-blue-200 hover:bg-blue-50"
                                                                onClick={handleEnterCorrectionMode}
                                                                disabled={decisionMutation.isPending}
                                                            >
                                                                <Pencil className="h-3.5 w-3.5 mr-2" />
                                                                Correct Submission
                                                            </Button>
                                                        )}
                                                    </div>

                                                    {reviewDecision === "REJECT" && (
                                                        <>
                                                            <div>
                                                                <p className="text-sm font-medium mb-2">Required action</p>
                                                                <Select
                                                                    value={selectedRejectionType || ""}
                                                                    onValueChange={(value) => setSelectedRejectionType(value as RejectionType)}
                                                                >
                                                                    <SelectTrigger>
                                                                        <SelectValue placeholder="Select required action" />
                                                                    </SelectTrigger>
                                                                    <SelectContent>
                                                                        <RejectionTypeSelectItems />
                                                                    </SelectContent>
                                                                </Select>
                                                            </div>

                                                            {selectedRejectionType && (
                                                                <div>
                                                                    <p className="text-sm font-medium mb-2">Reasons</p>
                                                                    {reasonCodesLoading ? (
                                                                        <p className="text-sm text-muted-foreground">Loading reasons...</p>
                                                                    ) : reasonCodes.length === 0 ? (
                                                                        <p className="text-sm text-muted-foreground">No active reasons configured for this action.</p>
                                                                    ) : (
                                                                        <div className="flex flex-wrap gap-2">
                                                                            {reasonCodes.map((rc) => (
                                                                                <Badge
                                                                                    key={rc.code}
                                                                                    variant={selectedReasons.includes(rc.code) ? "default" : "outline"}
                                                                                    className={`cursor-pointer ${selectedReasons.includes(rc.code) ? "ring-2 ring-primary/40" : ""}`}
                                                                                    onClick={() => {
                                                                                        setSelectedReasons((prev) => (
                                                                                            prev.includes(rc.code)
                                                                                                ? prev.filter((c) => c !== rc.code)
                                                                                                : [...prev, rc.code]
                                                                                        ));
                                                                                    }}
                                                                                >
                                                                                    {selectedReasons.includes(rc.code) && <CheckCircle2 className="h-3 w-3 mr-1" />}
                                                                                    {rc.label}
                                                                                </Badge>
                                                                            ))}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}

                                                            <div>
                                                                <p className="text-sm font-medium mb-2">
                                                                    Rejection notes {notesRequired ? "(required)" : "(optional)"}
                                                                </p>
                                                                <Textarea
                                                                    placeholder={notesRequired ? "Explain the issue for the operator..." : "Add additional context (optional)..."}
                                                                    value={rejectionNotes}
                                                                    onChange={(e) => setRejectionNotes(e.target.value)}
                                                                    rows={3}
                                                                />
                                                            </div>
                                                        </>
                                                    )}

                                                    <div>
                                                        <p className="text-sm font-medium mb-2">Operator feedback (optional)</p>
                                                        <Textarea
                                                            placeholder="Optional message shown to operator..."
                                                            value={feedback}
                                                            onChange={(e) => setFeedback(e.target.value)}
                                                            rows={3}
                                                        />
                                                    </div>

                                                    <div>
                                                        <p className="text-sm font-medium mb-2">Admin notes (internal only)</p>
                                                        <Textarea
                                                            placeholder="Internal note — e.g. why this was approved…"
                                                            value={adminNotesInput}
                                                            onChange={(e) => setAdminNotesInput(e.target.value)}
                                                            rows={2}
                                                        />
                                                    </div>

                                                    <Button
                                                        onClick={handleSubmitDecision}
                                                        disabled={decisionMutation.isPending}
                                                        variant={reviewDecision === "REJECT" ? "destructive" : "default"}
                                                        className={`w-full ${reviewDecision === "APPROVE" ? "bg-green-600 hover:bg-green-700" : ""} ${reviewDecision === "APPROVE_RETAILER_READY" ? "bg-blue-600 hover:bg-blue-700" : ""}`}
                                                    >
                                                        {decisionMutation.isPending ? (
                                                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                                        ) : reviewDecision === "APPROVE_RETAILER_READY" ? (
                                                            <ShieldCheck className="h-4 w-4 mr-2" />
                                                        ) : reviewDecision === "APPROVE" ? (
                                                            <CheckCircle className="h-4 w-4 mr-2" />
                                                        ) : (
                                                            <XCircle className="h-4 w-4 mr-2" />
                                                        )}
                                                        {reviewDecision === "APPROVE_RETAILER_READY"
                                                            ? "Submit Approval & Retailer Ready"
                                                            : reviewDecision === "APPROVE"
                                                                ? "Submit Approval"
                                                                : selectedRejectionType === "ADVISORY_ONLY" ? "Accept with concerns" : "Submit Rejection"}
                                                    </Button>
                                                </>
                                            )}
                                        </CardContent>
                                    </Card>

                                    <Card>
                                        <CardHeader className="pb-3">
                                            <div className="flex items-center justify-between gap-3">
                                                <CardTitle className="text-base">{claimedSubmissionTitle}</CardTitle>
                                                {!isDecisionLocked && (detail.formatRowConfirmations?.length || 0) > 0 ? (
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => {
                                                            const nextState: Record<number, ReviewFormatConfirmationStatus> = {};
                                                            (detail.formatRowConfirmations || []).forEach((row) => {
                                                                nextState[row.formatEvidenceId] = "CONFIRMED";
                                                            });
                                                            setFormatConfirmationStates(nextState);
                                                        }}
                                                    >
                                                        {claimedSubmissionActionLabel}
                                                    </Button>
                                                ) : null}
                                            </div>
                                        </CardHeader>
                                        <CardContent className="space-y-4">
                                            <div className="rounded-lg border">
                                                <div className="grid grid-cols-2 gap-0">
                                                    <div className="border-b p-4 sm:border-r">
                                                        <div className="text-xs uppercase tracking-wide text-muted-foreground">Expected units</div>
                                                        <p className="mt-2 text-xl font-semibold">
                                                            {totalExpectedUnits}
                                                        </p>
                                                    </div>
                                                    <div className="border-b p-4 sm:border-b">
                                                        <div className="text-xs uppercase tracking-wide text-muted-foreground">{reportedUnitsLabel}</div>
                                                        <p className="mt-2 text-xl font-semibold">
                                                            {totalReportedUnits}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="divide-y">
                                                    {claimedDeliveryRows.length > 0 ? (
                                                        claimedDeliveryRows.map((row) => {
                                                            const correctedVal = correctionValues[row.formatEvidenceId];
                                                            const displayCount = correctionMode && correctedVal !== undefined ? parseInt(correctedVal) || 0 : row.submittedCount;
                                                            const mismatch = displayCount !== row.expectedCount;
                                                            const currentStatus = formatConfirmationStates[row.formatEvidenceId] || row.finalStatus || row.prefillStatus;
                                                            return (
                                                                <div key={row.formatEvidenceId} className="space-y-3 p-3 text-sm">
                                                                    <div className="flex items-center justify-between gap-3">
                                                                        <div className="min-w-0">
                                                                            <p className="font-medium">{row.formatName}</p>
                                                                            <p className="mt-1 text-xs text-muted-foreground">
                                                                                {formatAiPresenceLabel(row.aiPresenceSupport, detail.submission.taskType)} · {formatAiCountLabel(row.aiCountSupport)}
                                                                            </p>
                                                                        </div>
                                                                        <div className="flex items-center gap-2">
                                                                            <Badge variant="outline">Expected {row.expectedCount}</Badge>
                                                                            {correctionMode && row.formatEvidenceId > 0 ? (
                                                                                <input
                                                                                    type="number"
                                                                                    min="0"
                                                                                    value={correctionValues[row.formatEvidenceId] ?? String(row.submittedCount ?? 0)}
                                                                                    onChange={(e) => setCorrectionValues(prev => ({ ...prev, [row.formatEvidenceId]: e.target.value }))}
                                                                                    className="w-16 h-7 text-xs font-mono text-center border-2 border-blue-300 rounded px-1 bg-blue-50 text-blue-900 focus:border-blue-500 focus:outline-none"
                                                                                />
                                                                            ) : (
                                                                                <Badge variant={mismatch ? "destructive" : "secondary"}>
                                                                                    {reportedCountBadgeLabel} {row.submittedCount}
                                                                                </Badge>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                    <div className="flex flex-wrap gap-2">
                                                                        <span className="flex items-center text-xs font-medium text-muted-foreground mr-1">
                                                                            {formatConfirmationSubjectLabel}
                                                                        </span>
                                                                        {(["CONFIRMED", "ISSUE", "UNCLEAR"] as ReviewFormatConfirmationStatus[]).map((status) => (
                                                                            <Button
                                                                                key={status}
                                                                                type="button"
                                                                                size="sm"
                                                                                variant={currentStatus === status ? formatConfirmationVariant(status) : "outline"}
                                                                                onClick={() => {
                                                                                    if (isDecisionLocked) return;
                                                                                    setFormatConfirmationStates((prev) => ({
                                                                                        ...prev,
                                                                                        [row.formatEvidenceId]: status,
                                                                                    }));
                                                                                }}
                                                                                disabled={isDecisionLocked}
                                                                            >
                                                                                {formatConfirmationLabel(status)}
                                                                            </Button>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            );
                                                        })
                                                    ) : (
                                                        <div className="p-3 text-sm text-muted-foreground">
                                                            No per-format submission counts recorded.
                                                        </div>
                                                    )}
                                                </div>
                                                {correctionMode && (
                                                    <div className="flex items-center justify-between gap-2 p-3 rounded-md border-2 border-blue-200 bg-blue-50/50">
                                                        <p className="text-xs text-blue-700 font-medium">
                                                            Editing delivered quantities
                                                        </p>
                                                        <div className="flex items-center gap-2">
                                                            <Button
                                                                type="button"
                                                                size="sm"
                                                                variant="outline"
                                                                onClick={handleCancelCorrectionMode}
                                                                disabled={bulkCorrectionMutation.isPending}
                                                            >
                                                                Cancel
                                                            </Button>
                                                            <Button
                                                                type="button"
                                                                size="sm"
                                                                className="bg-blue-600 hover:bg-blue-700"
                                                                onClick={handleSaveCorrectionMode}
                                                                disabled={bulkCorrectionMutation.isPending}
                                                            >
                                                                {bulkCorrectionMutation.isPending ? (
                                                                    <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                                                                ) : (
                                                                    <Check className="h-3.5 w-3.5 mr-1.5" />
                                                                )}
                                                                Save Corrections
                                                            </Button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            {(detail.submission.installerNote || detail.submission.notes) ? (
                                                <div className="rounded-lg border bg-muted/20 p-4">
                                                    <div className="text-xs uppercase tracking-wide text-muted-foreground">Submission notes</div>
                                                    {detail.submission.installerNote ? (
                                                        <p className="mt-2 text-sm">{detail.submission.installerNote}</p>
                                                    ) : null}
                                                    {detail.submission.notes ? (
                                                        <p className="mt-2 text-sm text-muted-foreground">{detail.submission.notes}</p>
                                                    ) : null}
                                                </div>
                                            ) : null}
                                        </CardContent>
                                    </Card>
                                </div>
                            </div>
                                </TabsContent>

                                <TabsContent value="ai-feedback" className="space-y-6">
                                    <div className="grid gap-4 xl:grid-cols-[1.45fr_0.95fr]">
                                        <div className="space-y-4">
                                        {showsSubmissionReferencePanel(detail.submission.taskType) && countReferenceKindGroups(detail.sourcePostingReferences) > 0 ? (
                                            <RemovalSourcePostingReferences
                                                references={detail.sourcePostingReferences ?? []}
                                            />
                                        ) : null}
                                        <CurrentSubmissionEvidencePanel
                                            taskType={detail.submission.taskType}
                                            attachedCount={detail.images.length}
                                            referenceCount={countReferenceKindGroups(detail.sourcePostingReferences)}
                                        >
                                                {detail.images.length > 0 && detail.images[0] ? (
                                                    <div className="space-y-3">
                                                        <a
                                                            href={detail.images[0].secureUrl}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="group block overflow-hidden rounded-xl border bg-muted/10"
                                                        >
                                                            <img
                                                                src={detail.images[0].secureUrl}
                                                                alt="Current submission evidence under review"
                                                                className="h-[420px] w-full object-cover transition-transform group-hover:scale-[1.01]"
                                                            />
                                                        </a>
                                                    </div>
                                                ) : (
                                                    <div className="flex h-40 items-center justify-center rounded-lg border border-dashed text-sm text-muted-foreground">
                                                        No photos attached.
                                                    </div>
                                                )}
                                        </CurrentSubmissionEvidencePanel>
                                        </div>

                                        <div className="space-y-4">
                                            <Card>
                                                <CardHeader className="pb-3">
                                                    <CardTitle className="text-base">AI Feedback Status</CardTitle>
                                                </CardHeader>
                                                <CardContent className="space-y-3 text-sm">
                                                    <div className="rounded-md border bg-muted/20 p-3">
                                                        <div className="flex items-center justify-between gap-3">
                                                            <div>
                                                                <p className="font-medium">{aiFeedbackStatusLabel}</p>
                                                                <p className="text-xs text-muted-foreground">
                                                                    Compliance review and AI training feedback are tracked as separate tasks.
                                                                </p>
                                                            </div>
                                                            <Badge variant="outline">
                                                                {detail.formatReviewLabels?.length || 0} / {expectedFormatContexts.length || claimedDeliveryRows.length} formats labeled
                                                            </Badge>
                                                        </div>
                                                    </div>

                                                    <div className="grid gap-2 sm:grid-cols-2">
                                                        <Button
                                                            type="button"
                                                            variant="outline"
                                                            onClick={() => setReviewModalTab("ai-feedback")}
                                                        >
                                                            Continue AI Feedback
                                                        </Button>
                                                        <Button
                                                            type="button"
                                                            variant="outline"
                                                            onClick={() => setReviewModalTab("compliance")}
                                                        >
                                                            Return To Compliance
                                                        </Button>
                                                    </div>
                                                </CardContent>
                                            </Card>

                                            <Card>
                                                <CardHeader className="pb-3">
                                                    <CardTitle className="text-base">Expected Format Set</CardTitle>
                                                </CardHeader>
                                                <CardContent className="space-y-3">
                                                    <p className="text-sm text-muted-foreground">
                                                        This submission should be reviewed against the full set of expected formats, not a single primary format.
                                                    </p>
                                                    <div className="flex flex-wrap gap-2">
                                                        {expectedFormatContexts.length > 0 ? expectedFormatContexts.map((format) => (
                                                            <Badge key={`${format.mediaFormatId ?? "unknown"}-${format.expectedFormatName}`} variant="secondary">
                                                                {format.expectedFormatName} · Qty {format.expectedQuantity}
                                                            </Badge>
                                                        )) : (
                                                            <span className="text-sm text-muted-foreground">No expected formats resolved for this submission.</span>
                                                        )}
                                                    </div>
                                                </CardContent>
                                            </Card>
                                        </div>
                                    </div>

                            <div className="rounded-lg border px-4">
                                <div className="border-b py-4">
                                    <h3 className="text-sm font-semibold tracking-wide text-foreground">Verification Intelligence</h3>
                                </div>
                            <Accordion type="multiple">
                                    <AccordionItem value="supporting-context">
                                        <AccordionTrigger className="text-sm">Supporting Context</AccordionTrigger>
                                        <AccordionContent className="space-y-4">
                                            <div className="grid gap-4 xl:grid-cols-[1.1fr_0.9fr]">
                                            <Card>
                                                <CardHeader className="pb-3">
                                                    <CardTitle className="text-base">
                                                        {isRemovalTaskType(detail.submission.taskType) ? "Expected Removal Set" : "Expected Format Set"}
                                                    </CardTitle>
                                                </CardHeader>
                                                <CardContent className="space-y-4">
                                                    {expectedFormatContexts.length > 0 ? (
                                                        <div className="space-y-4">
                                                            {expectedFormatContexts.map((format) => (
                                                                <div key={`${format.mediaFormatId ?? "unknown"}-${format.expectedFormatName}`} className="rounded-lg border p-4 space-y-3">
                                                                    <div className="flex items-start justify-between gap-3">
                                                                        <div className="space-y-1">
                                                                            <p className="text-base font-semibold leading-tight">{format.expectedFormatName}</p>
                                                                            {format.reviewerDescription ? (
                                                                                <p className="text-sm text-muted-foreground">{format.reviewerDescription}</p>
                                                                            ) : null}
                                                                        </div>
                                                                        <Badge variant="secondary">Qty {format.expectedQuantity}</Badge>
                                                                    </div>
                                                                    <div className="flex flex-wrap gap-2">
                                                                        {format.typicalDimensions ? <Badge variant="outline">{format.typicalDimensions}</Badge> : null}
                                                                        {format.visualCues.slice(0, 3).map((cue) => (
                                                                            <Badge key={cue} variant="outline">{cue}</Badge>
                                                                        ))}
                                                                    </div>
                                                                    {format.placementGuidance ? (
                                                                        <p className="text-sm text-muted-foreground">{format.placementGuidance}</p>
                                                                    ) : null}
                                                                    {format.exampleImages.length > 0 ? (
                                                                        <div className="grid grid-cols-2 gap-3">
                                                                            {format.exampleImages.slice(0, 2).map((image) => (
                                                                                <a
                                                                                    key={`${image.id}-${image.imageUrl}`}
                                                                                    href={image.imageUrl}
                                                                                    target="_blank"
                                                                                    rel="noopener noreferrer"
                                                                                    className="group rounded-md border overflow-hidden bg-background"
                                                                                >
                                                                                    <img
                                                                                        src={image.imageUrl}
                                                                                        alt={image.caption || format.expectedFormatName}
                                                                                        className="h-24 w-full object-cover transition-transform group-hover:scale-[1.02]"
                                                                                    />
                                                                                    <div className="space-y-1 p-2">
                                                                                        <Badge variant={image.role === "good_example" ? "default" : "outline"} className="text-[10px]">
                                                                                            {roleLabel(image.role)}
                                                                                        </Badge>
                                                                                        {image.caption ? (
                                                                                            <p className="line-clamp-2 text-xs text-muted-foreground">{image.caption}</p>
                                                                                        ) : null}
                                                                                    </div>
                                                                                </a>
                                                                            ))}
                                                                        </div>
                                                                    ) : null}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <p className="text-sm text-muted-foreground">No expected formats resolved for this submission.</p>
                                                    )}
                                                </CardContent>
                                            </Card>

                                            <div className="space-y-4">
                                                <Card className={correctionMode ? "ring-2 ring-blue-300" : ""}>
                                                    <CardHeader className="pb-3">
                                                        <div className="flex items-center justify-between">
                                                            <CardTitle className="text-base">Format Evidence</CardTitle>
                                                            {correctionMode && (
                                                                <Badge className="bg-blue-100 text-blue-700 border-blue-200">
                                                                    <Pencil className="h-3 w-3 mr-1" />
                                                                    Editing
                                                                </Badge>
                                                            )}
                                                        </div>
                                                    </CardHeader>
                                                    <CardContent className="space-y-3">
                                                        <div className="border rounded-lg overflow-hidden">
                                                            <Table>
                                                                <TableHeader className="bg-muted/50">
                                                                    <TableRow>
                                                                        <TableHead className="h-8 py-0 text-[10px] uppercase font-bold tracking-wider">Format</TableHead>
                                                                        <TableHead className="h-8 py-0 text-[10px] uppercase font-bold tracking-wider text-right">Planned</TableHead>
                                                                        <TableHead className="h-8 py-0 text-[10px] uppercase font-bold tracking-wider text-right">
                                                                            {detail.submission.taskType === 'REMOVAL' ? 'Removed' : 'Delivered'}
                                                                        </TableHead>
                                                                    </TableRow>
                                                                </TableHeader>
                                                                <TableBody>
                                                                    {detail.formatEvidence && detail.formatEvidence.length > 0 ? (
                                                                        <>
                                                                            {detail.formatEvidence.map((ev) => {
                                                                                const actualCount = ev.actualCount != null ? ev.actualCount : 0;
                                                                                const hasDiscrepancy = actualCount !== ev.expectedCount;
                                                                                const isEditing = editingFormatId === ev.id;
                                                                                const canEdit = ev.id > 0;
                                                                                return (
                                                                                    <TableRow key={ev.id} className="h-9">
                                                                                        <TableCell className="py-1 text-sm font-medium">
                                                                                            <div className="flex items-center gap-1.5">
                                                                                                {ev.formatName}
                                                                                                {(hasDiscrepancy || ev.discrepancyReason) && (
                                                                                                    <TooltipProvider>
                                                                                                        <Tooltip>
                                                                                                            <TooltipTrigger>
                                                                                                                <AlertTriangle className="h-3 w-3 text-orange-500" />
                                                                                                            </TooltipTrigger>
                                                                                                            <TooltipContent>
                                                                                                                <p className="text-xs">
                                                                                                                    {ev.discrepancyReason || `Expected ${ev.expectedCount}, got ${ev.actualCount ?? 0}`}
                                                                                                                </p>
                                                                                                            </TooltipContent>
                                                                                                        </Tooltip>
                                                                                                    </TooltipProvider>
                                                                                                )}
                                                                                                {ev.hasCorrection && ev.latestCorrection && (
                                                                                                    <TooltipProvider>
                                                                                                        <Tooltip>
                                                                                                            <TooltipTrigger>
                                                                                                                <History className="h-3 w-3 text-blue-500" />
                                                                                                            </TooltipTrigger>
                                                                                                            <TooltipContent>
                                                                                                                <p className="text-xs">
                                                                                                                    Corrected by {ev.latestCorrection.correctedByName}: {ev.latestCorrection.oldActualCount ?? 0} → {ev.latestCorrection.newActualCount}
                                                                                                                    {ev.latestCorrection.reason && <><br />{ev.latestCorrection.reason}</>}
                                                                                                                </p>
                                                                                                            </TooltipContent>
                                                                                                        </Tooltip>
                                                                                                    </TooltipProvider>
                                                                                                )}
                                                                                            </div>
                                                                                        </TableCell>
                                                                                        <TableCell className="py-1 text-right">
                                                                                            <Badge variant="outline" className="text-[10px] h-5 px-1.5 font-mono min-w-[20px] justify-center">
                                                                                                {ev.expectedCount}
                                                                                            </Badge>
                                                                                        </TableCell>
                                                                                        <TableCell className="py-1 text-right">
                                                                                            {correctionMode && canEdit ? (
                                                                                                <input
                                                                                                    type="number"
                                                                                                    min="0"
                                                                                                    value={correctionValues[ev.id] ?? String(actualCount)}
                                                                                                    onChange={(e) => setCorrectionValues(prev => ({ ...prev, [ev.id]: e.target.value }))}
                                                                                                    className="w-14 h-6 text-xs font-mono text-center border-2 border-blue-300 rounded px-1 bg-blue-50 focus:border-blue-500 focus:outline-none"
                                                                                                    title={quantityLabel}
                                                                                                    aria-label={quantityLabel}
                                                                                                />
                                                                                            ) : isEditing ? (
                                                                                                <div className="flex items-center justify-end gap-1">
                                                                                                    <input
                                                                                                        type="number"
                                                                                                        min="0"
                                                                                                        value={editValue}
                                                                                                        onChange={(e) => setEditValue(e.target.value)}
                                                                                                        className="w-12 h-5 text-[10px] font-mono text-center border rounded px-1"
                                                                                                        autoFocus
                                                                                                        title={quantityLabel}
                                                                                                        aria-label={quantityLabel}
                                                                                                        onKeyDown={(e) => {
                                                                                                            if (e.key === 'Enter') handleSaveEdit(ev);
                                                                                                            if (e.key === 'Escape') handleCancelEdit();
                                                                                                        }}
                                                                                                    />
                                                                                                    <button
                                                                                                        onClick={() => handleSaveEdit(ev)}
                                                                                                        className="p-0.5 hover:bg-green-100 rounded"
                                                                                                        disabled={quantityCorrectionMutation.isPending}
                                                                                                        title="Save quantity"
                                                                                                        aria-label="Save quantity"
                                                                                                    >
                                                                                                        {quantityCorrectionMutation.isPending ? (
                                                                                                            <Loader2 className="h-3 w-3 animate-spin" />
                                                                                                        ) : (
                                                                                                            <Check className="h-3 w-3 text-green-600" />
                                                                                                        )}
                                                                                                    </button>
                                                                                                    <button
                                                                                                        onClick={handleCancelEdit}
                                                                                                        className="p-0.5 hover:bg-red-100 rounded"
                                                                                                        disabled={quantityCorrectionMutation.isPending}
                                                                                                        title="Cancel editing"
                                                                                                        aria-label="Cancel editing"
                                                                                                    >
                                                                                                        <X className="h-3 w-3 text-red-600" />
                                                                                                    </button>
                                                                                                </div>
                                                                                            ) : (
                                                                                                <div className="flex items-center justify-end gap-1 group">
                                                                                                    <Badge
                                                                                                        variant={hasDiscrepancy ? "destructive" : (actualCount > 0 ? "success" : "outline")}
                                                                                                        className={`text-[10px] h-5 px-1.5 font-mono min-w-[20px] justify-center ${hasDiscrepancy
                                                                                                            ? "!bg-red-600 !text-white !border-red-700"
                                                                                                            : actualCount > 0
                                                                                                                ? "!bg-emerald-100 !text-emerald-900 !border-emerald-200"
                                                                                                                : "!bg-slate-100 !text-slate-900 !border-slate-300"
                                                                                                        }`}
                                                                                                    >
                                                                                                        {actualCount}
                                                                                                    </Badge>
                                                                                                    {canEdit && !correctionMode && (
                                                                                                        <button
                                                                                                            onClick={() => handleStartEdit(ev)}
                                                                                                            className="p-0.5 hover:bg-slate-100 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                                                                                                            title={`Edit ${quantityLabel.toLowerCase()}`}
                                                                                                        >
                                                                                                            <Pencil className="h-3 w-3 text-slate-500" />
                                                                                                        </button>
                                                                                                    )}
                                                                                                </div>
                                                                                            )}
                                                                                        </TableCell>
                                                                                    </TableRow>
                                                                                );
                                                                            })}
                                                                            <TableRow className="bg-muted/20 font-bold h-9">
                                                                                <TableCell className="py-1 text-sm">Total</TableCell>
                                                                                <TableCell className="py-1 text-right text-sm">
                                                                                    <Badge variant="outline" className="text-[10px] h-5 px-1.5 font-mono min-w-[20px] justify-center">
                                                                                        {detail.formatEvidence.reduce((acc, curr) => acc + curr.expectedCount, 0)}
                                                                                    </Badge>
                                                                                </TableCell>
                                                                                <TableCell className="py-1 text-right text-sm">
                                                                                    {(() => {
                                                                                        const totalExpected = detail.formatEvidence.reduce((acc, curr) => acc + curr.expectedCount, 0);
                                                                                        const totalActual = detail.formatEvidence.reduce((acc, curr) => acc + (curr.actualCount != null ? curr.actualCount : 0), 0);
                                                                                        const totalsDiscrepant = totalExpected !== totalActual;
                                                                                        return (
                                                                                            <Badge
                                                                                                variant={totalsDiscrepant ? "destructive" : (totalActual > 0 ? "success" : "outline")}
                                                                                                className={`text-[10px] h-5 px-1.5 font-mono min-w-[20px] justify-center ${totalsDiscrepant
                                                                                                    ? "!bg-red-600 !text-white !border-red-700"
                                                                                                    : totalActual > 0
                                                                                                        ? "!bg-emerald-100 !text-emerald-900 !border-emerald-200"
                                                                                                        : "!bg-slate-100 !text-slate-900 !border-slate-300"
                                                                                                }`}
                                                                                            >
                                                                                                {totalActual}
                                                                                            </Badge>
                                                                                        );
                                                                                    })()}
                                                                                </TableCell>
                                                                            </TableRow>
                                                                        </>
                                                                    ) : (
                                                                        <TableRow>
                                                                            <TableCell colSpan={3} className="text-center py-4 text-xs text-muted-foreground italic">
                                                                                No granular format data recorded
                                                                            </TableCell>
                                                                        </TableRow>
                                                                    )}
                                                                </TableBody>
                                                            </Table>
                                                        </div>

                                                        {correctionMode && (
                                                            <div className="flex items-center justify-between gap-2 mt-3 p-3 rounded-md border-2 border-blue-200 bg-blue-50/50">
                                                                <p className="text-xs text-blue-700 font-medium">
                                                                    Editing delivered quantities — changes are saved to audit trail
                                                                </p>
                                                                <div className="flex items-center gap-2">
                                                                    <Button
                                                                        type="button"
                                                                        variant="outline"
                                                                        size="sm"
                                                                        onClick={handleCancelCorrectionMode}
                                                                        disabled={bulkCorrectionMutation.isPending}
                                                                    >
                                                                        Cancel
                                                                    </Button>
                                                                    <Button
                                                                        type="button"
                                                                        size="sm"
                                                                        className="bg-blue-600 hover:bg-blue-700"
                                                                        onClick={handleSaveCorrectionMode}
                                                                        disabled={bulkCorrectionMutation.isPending}
                                                                    >
                                                                        {bulkCorrectionMutation.isPending ? (
                                                                            <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                                                                        ) : (
                                                                            <Check className="h-3.5 w-3.5 mr-1.5" />
                                                                        )}
                                                                        Save Corrections
                                                                    </Button>
                                                                </div>
                                                            </div>
                                                        )}

                                                    </CardContent>
                                                </Card>

                                                <Card>
                                                    <CardHeader className="pb-3">
                                                        <CardTitle className="text-base">
                                                            {isRemovalTaskType(detail.submission.taskType) ? "Removal Instructions" : "Posting Instructions"}
                                                        </CardTitle>
                                                    </CardHeader>
                                                    <CardContent className="space-y-3">
                                                        {postingInstructionsText ? (
                                                            <div
                                                                className="text-sm leading-6 prose prose-sm max-w-none"
                                                                dangerouslySetInnerHTML={{
                                                                    __html: DOMPurify.sanitize(postingInstructionsText),
                                                                }}
                                                            />
                                                        ) : (
                                                            <p className="text-sm text-muted-foreground">
                                                                {isRemovalTaskType(detail.submission.taskType)
                                                                    ? "No specific removal instructions provided."
                                                                    : "No specific posting instructions provided."}
                                                            </p>
                                                        )}
                                                        {postingInstructionsImageUrl && (
                                                            <a href={postingInstructionsImageUrl} target="_blank" rel="noopener noreferrer">
                                                                <img
                                                                    src={postingInstructionsImageUrl}
                                                                    alt="Posting instruction"
                                                                    className="w-full max-h-56 object-contain rounded-md border bg-white"
                                                                />
                                                            </a>
                                                        )}
                                                    </CardContent>
                                                </Card>
                                            </div>
                                        </div>
                                    </AccordionContent>
                                </AccordionItem>

                                <AccordionItem value="diagnostics">
                                    <AccordionTrigger className="text-sm">Diagnostics</AccordionTrigger>
                                    <AccordionContent className="space-y-4">
                                        <VerificationSummaryPanel
                                            verificationSummary={detail.verificationSummary ?? null}
                                            formatContext={detail.formatContext ?? null}
                                        />

                                        {((canViewAiTriage && detail.submission.taskType === "REMOVAL") || modalAiVerdict != null || modalAiVerdictReasons.length > 0) && (
                                            <div className="rounded-md border bg-muted/20 p-3 text-xs space-y-1">
                                                <p>
                                                    <span className="font-medium text-foreground">AI Triage:</span>{" "}
                                                    <span className="font-medium text-foreground">
                                                        {modalAiVerdict
                                                            ? (modalAiVerdict === "PARTIAL_FAIL" ? "PARTIAL FAIL" : modalAiVerdict)
                                                            : "Not triaged yet"}
                                                    </span>
                                                </p>
                                                {modalAiVerdictReasons.length > 0 && (
                                                    <div>
                                                        <p className="font-medium text-foreground">Why:</p>
                                                        <ul className="list-disc pl-4 text-muted-foreground">
                                                            {modalAiVerdictReasons.slice(0, 4).map((reason) => (
                                                                <li key={reason}>{reason}</li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )}
                                                {!modalAiVerdict && modalAiVerdictReasons.length === 0 && (
                                                    <p className="text-muted-foreground">
                                                        This submission has not produced an AI triage result yet.
                                                    </p>
                                                )}
                                            </div>
                                        )}

                                        {detail.aiVerification && (
                                            <div className="rounded-md border bg-blue-50/60 p-3 text-xs space-y-2">
                                                <div className="flex items-center justify-between gap-2">
                                                    <p className="font-medium text-foreground">AI Verification Evidence</p>
                                                    <Badge variant="outline" className="text-[10px]">
                                                        Confidence {Math.round((detail.aiVerification.confidence ?? 0) * 100)}%
                                                    </Badge>
                                                </div>
                                                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                                    <div>
                                                        <span className="text-muted-foreground">Presence</span>
                                                        <p className="font-medium">{detail.aiVerification.presenceStatus}</p>
                                                    </div>
                                                    <div>
                                                        <span className="text-muted-foreground">Quality</span>
                                                        <p className="font-medium">{detail.aiVerification.qualityStatus}</p>
                                                    </div>
                                                    <div>
                                                        <span className="text-muted-foreground">Overall</span>
                                                        <p className="font-medium">{detail.aiVerification.overallStatus}</p>
                                                    </div>
                                                    <div>
                                                        <span className="text-muted-foreground">Evidence</span>
                                                        <p className="font-medium">{detail.aiVerification.evidenceSufficiency}</p>
                                                    </div>
                                                </div>
                                                <div className="text-muted-foreground">
                                                    Unknown format detections: <span className="font-medium text-foreground">{detail.aiVerification.unknownFormatCount}</span>
                                                    <span className="ml-1">(review escalator only, never counted as compliance evidence)</span>
                                                </div>
                                                {detail.aiVerification.explanation && (
                                                    <p className="text-muted-foreground">{detail.aiVerification.explanation}</p>
                                                )}
                                                {Array.isArray(detail.aiVerificationOverrides) && detail.aiVerificationOverrides.length > 0 && (
                                                    <div className="pt-1 border-t border-blue-200/60">
                                                        <p className="font-medium text-foreground mb-1">AI Overrides</p>
                                                        <ul className="space-y-1 text-muted-foreground">
                                                            {detail.aiVerificationOverrides.slice(0, 3).map((override) => (
                                                                <li key={override.id}>
                                                                    {override.targetType}: {override.fromStatus || "n/a"} → {override.toStatus}
                                                                    {override.reasonCode ? ` (${override.reasonCode})` : ""}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        <AiTriageFeedbackPanel
                                            submissionId={detail.submission.id}
                                            taskType={detail.submission.taskType}
                                            reviewerDecision={reviewDecision === "APPROVE_RETAILER_READY" ? "APPROVE" : reviewDecision}
                                            canView={canViewAiTriage}
                                            canRun={canRunAiTriage}
                                            useReviewApi
                                        />
                                    </AccordionContent>
                                </AccordionItem>

                                <AccordionItem value="training-tools">
                                    <AccordionTrigger className="text-sm">Training Tools</AccordionTrigger>
                                    <AccordionContent className="space-y-4">
                                        <div className="flex flex-wrap items-center justify-between gap-3">
                                            <div className="text-sm text-muted-foreground">
                                                {favouriteImageIds.length} image{favouriteImageIds.length === 1 ? "" : "s"} selected for advertiser reporting
                                            </div>
                                            <div className="flex flex-wrap gap-2">
                                                {expectedFormatContexts.length > 0 && (
                                                    <Select value={selectedTrainingFormatId} onValueChange={setSelectedTrainingFormatId}>
                                                        <SelectTrigger className="w-[240px]">
                                                            <SelectValue placeholder="Select format for AI feedback" />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            {expectedFormatContexts
                                                                .filter((format) => format.mediaFormatId != null)
                                                                .map((format) => (
                                                                    <SelectItem key={format.mediaFormatId} value={String(format.mediaFormatId)}>
                                                                        {format.expectedFormatName} · Qty {format.expectedQuantity}
                                                                    </SelectItem>
                                                                ))}
                                                        </SelectContent>
                                                    </Select>
                                                )}
                                                {latestDecision?.decision === "APPROVE" && (
                                                    <Button
                                                        onClick={() => {
                                                            if (!selectedSubmission) return;
                                                            updateFavouritesMutation.mutate({
                                                                postingEventId: selectedSubmission,
                                                                favouriteImageIds,
                                                            });
                                                        }}
                                                        disabled={updateFavouritesMutation.isPending}
                                                        variant="outline"
                                                    >
                                                        {updateFavouritesMutation.isPending ? (
                                                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                                        ) : (
                                                            <Star className="h-4 w-4 mr-2" />
                                                        )}
                                                        Save Favourites
                                                    </Button>
                                                )}
                                                {trainingTargetFormat?.mediaFormatId ? (
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        disabled={favouriteImageIds.length === 0 || promoteExamplesMutation.isPending}
                                                        onClick={() => {
                                                            if (!selectedSubmission || !trainingTargetFormat?.mediaFormatId) return;
                                                            promoteExamplesMutation.mutate({
                                                                postingEventId: selectedSubmission,
                                                                evidenceImageIds: favouriteImageIds,
                                                                mediaFormatId: trainingTargetFormat.mediaFormatId,
                                                            });
                                                        }}
                                                    >
                                                        {promoteExamplesMutation.isPending ? (
                                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                        ) : (
                                                            <ImageIcon className="mr-2 h-4 w-4" />
                                                        )}
                                                        Promote Selected As Examples
                                                    </Button>
                                                ) : null}
                                            </div>
                                        </div>

                                        {trainingTargetFormat?.expectedFormatName ? (
                                            <div className="rounded-md border bg-blue-50/40 px-3 py-2 text-xs text-muted-foreground">
                                                Select the photo(s) that specifically support the expected{" "}
                                                <span className="font-medium text-foreground">{trainingTargetFormat.expectedFormatName}</span>.
                                                These selections are stored in the background training corpus even if the format already has curated examples.
                                            </div>
                                        ) : null}

                                        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                                            {detail.images.map((img) => {
                                                const isImageAsset = img.assetType === "photo" || img.assetType === "image";
                                                return (
                                                    <div key={img.id} className="relative rounded-lg border p-2">
                                                        <a href={img.secureUrl} target="_blank" rel="noopener noreferrer">
                                                            <img
                                                                src={img.secureUrl}
                                                                alt="Evidence"
                                                                className="h-36 w-full object-cover rounded-md border hover:opacity-90 transition-opacity"
                                                            />
                                                        </a>
                                                        <TooltipProvider>
                                                            <Tooltip>
                                                                <TooltipTrigger asChild>
                                                                    <button
                                                                        type="button"
                                                                        disabled={!isImageAsset}
                                                                        title={isImageAsset ? "Toggle favourite for reporting" : "Only images can be favourited"}
                                                                        aria-label={isImageAsset ? "Toggle favourite for reporting" : "Only images can be favourited"}
                                                                        onClick={(event) => {
                                                                            event.preventDefault();
                                                                            event.stopPropagation();
                                                                            if (!isImageAsset) return;
                                                                            setFavouriteImageIds((prev) => {
                                                                                if (prev.includes(img.id)) {
                                                                                    return prev.filter((id) => id !== img.id);
                                                                                }
                                                                                return [...prev, img.id];
                                                                            });
                                                                        }}
                                                                        className={`absolute right-4 top-4 rounded-full p-1.5 shadow-sm border transition-colors ${favouriteImageIds.includes(img.id)
                                                                            ? "bg-yellow-400 text-yellow-900 border-yellow-500"
                                                                            : "bg-white/90 text-muted-foreground border-border hover:bg-white"
                                                                        } ${!isImageAsset ? "opacity-50 cursor-not-allowed" : ""}`}
                                                                    >
                                                                        <Star className="h-4 w-4" />
                                                                    </button>
                                                                </TooltipTrigger>
                                                                <TooltipContent>
                                                                    <p className="text-xs">
                                                                        {isImageAsset ? "Include in advertiser reporting" : "Only image assets can be favourited"}
                                                                    </p>
                                                                </TooltipContent>
                                                            </Tooltip>
                                                        </TooltipProvider>
                                                        <TooltipProvider>
                                                            <Tooltip>
                                                                <TooltipTrigger asChild>
                                                                    <button
                                                                        type="button"
                                                                        disabled={deleteImageMutation.isPending}
                                                                        title="Retract this image from the submission"
                                                                        aria-label="Retract this image from the submission"
                                                                        onClick={(event) => {
                                                                            event.preventDefault();
                                                                            event.stopPropagation();
                                                                            if (selectedSubmission == null) return;
                                                                            const confirmed = window.confirm(
                                                                                "Retract this image from the submission? It will be hidden from review and reporting but kept in the audit history."
                                                                            );
                                                                            if (!confirmed) return;
                                                                            deleteImageMutation.mutate({ postingEventId: selectedSubmission, assetId: img.id });
                                                                        }}
                                                                        className="absolute left-4 top-4 rounded-full p-1.5 shadow-sm border bg-white/90 text-red-600 border-border hover:bg-red-50 transition-colors disabled:opacity-50"
                                                                    >
                                                                        {deleteImageMutation.isPending ? (
                                                                            <Loader2 className="h-4 w-4 animate-spin" />
                                                                        ) : (
                                                                            <Trash2 className="h-4 w-4" />
                                                                        )}
                                                                    </button>
                                                                </TooltipTrigger>
                                                                <TooltipContent>
                                                                    <p className="text-xs">Remove image from this submission</p>
                                                                </TooltipContent>
                                                            </Tooltip>
                                                        </TooltipProvider>
                                                        {trainingTargetFormat?.expectedFormatName && isImageAsset && (
                                                            <label className="mt-2 flex items-center gap-2 rounded-md border bg-white/90 px-2 py-1 text-[11px] text-muted-foreground">
                                                                <Checkbox
                                                                    checked={supportingFormatImageIds.includes(img.id)}
                                                                    onCheckedChange={(checked) => {
                                                                        setSupportingFormatImageIds((prev) => {
                                                                            if (checked) {
                                                                                return prev.includes(img.id) ? prev : [...prev, img.id];
                                                                            }
                                                                            return prev.filter((id) => id !== img.id);
                                                                        });
                                                                    }}
                                                                />
                                                                Supports expected format
                                                            </label>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                            {detail.images.length === 0 && (
                                                <div className="col-span-full flex h-24 items-center justify-center rounded-md border border-dashed text-xs text-muted-foreground">
                                                    No photos attached.
                                                </div>
                                            )}
                                        </div>
                                        <div className="mt-3 flex items-center gap-2">
                                            <input
                                                id="review-add-photo-input"
                                                type="file"
                                                accept="image/*"
                                                className="hidden"
                                                disabled={addImageMutation.isPending || selectedSubmission == null}
                                                onChange={(event) => {
                                                    const file = event.target.files?.[0];
                                                    event.target.value = "";
                                                    if (!file || selectedSubmission == null) return;
                                                    addImageMutation.mutate({ postingEventId: selectedSubmission, file });
                                                }}
                                            />
                                            <Button
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                disabled={addImageMutation.isPending || selectedSubmission == null}
                                                onClick={() => document.getElementById("review-add-photo-input")?.click()}
                                            >
                                                {addImageMutation.isPending ? (
                                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                ) : (
                                                    <Upload className="mr-2 h-4 w-4" />
                                                )}
                                                Add photo
                                            </Button>
                                            <span className="text-[11px] text-muted-foreground">
                                                Attach a replacement or additional image to this submission.
                                            </span>
                                        </div>
                                    </AccordionContent>
                                </AccordionItem>
                            </Accordion>
                            </div>
                                </TabsContent>
                            </Tabs>
                        </div>
                    ) : (
                        <div className="text-center py-8 text-muted-foreground">
                            Submission not found or failed to load.
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            <HeldEvidenceReviewModal
                mediaOperationId={selectedHeldMediaOperationId}
                onClose={() => setSelectedHeldMediaOperationId(null)}
            />
        </TrackerLayout>
    );
}
